import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import ConfirmHeader from "../../components/ConfirmHeader";
import "../../utils/sheetEnter"; // .sheet-enter-wait (holds the Done pop until painted)
import { pauseNavBlur } from "../../utils/navBlur";
import { isAmountHabit, unitOf, addToLog, loggedAmount, formatProgress } from "../../utils/habitUnits";
import { AddAmountSheet } from "../../components/AddAmountSheet";
import { useSheetDrag } from "../../utils/sheetDrag";
import { createPortal } from "react-dom";
import { toast as sonnerToast } from "sonner";
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors, useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, CheckCircle, PlayCircle, PauseCircle,
  RotateCcw, Sparkles, GripVertical, FolderPlus, ListChecks, ListTodo,
  Pencil, Plus, Minus, Check, X, Timer, Info, Trash2, Eye, EyeOff, StickyNote,
  ArrowLeftRight, Undo2, MinusCircle, MoreHorizontal,
} from 'lucide-react';
import { CheckCircle as PhCheckCircle } from '../../PhosphorIcons';
import { S, CONFIRM_STACK, CONFIRM_DANGER, CONFIRM_CANCEL, CONFIRM_PRIMARY, CONFIRM_COLUMN, CONFIRM_OVERLAY, CONFIRM_DIM, CONFIRM_CARD } from "../../theme/sharedStyles";
import { vividPastel } from "../../theme/colorEngine";
import { clockInInk, ciTitleBtn } from "../../theme/clockInInk";
import { load, save } from "../../utils/storage";
import { todayStr, localDateStr, fmt } from "../../utils/dateHelpers";
import { isHabitDueOn, isHabitDoneOn } from "../../utils/habitLogic";
import { isTaskOccurringOn, isTaskDoneOn, toggleTaskDoneOn } from "../../utils/taskLogic";
import { TIMER_ALARM_SOUND_FILE } from "../../constants/misc";
import { HabitIcon } from "../../components/HabitIcon";
import { TabHeader } from "../../components/TabHeader";
import { HabitCheckbox } from "../../components/HabitCheckbox";
import { EmojiPicker } from "../../components/pickers/EmojiPicker";
import { ItemDetailsModal } from "../../components/ItemDetailsModal";
import { withSessionNote } from "../../utils/sessionGrouping";
import { minutesBetween, withSessionTimes, withoutSession, habitLogsAfterSessionChange, pastSession } from "../../utils/sessionEdit";
import { SessionSheetHost } from "../../components/SessionSheet";
import { NoteAddPill, SessionNoteBar } from "../../components/SessionNoteField";
import { primeKeyboard } from "../../capacitor/keyboardNow";
import { categoryPlanItem, isCategoryItem, categorySession, rememberedCategory, rememberCategory, SESSION_TITLE_MAX } from "../../utils/clockInCategory";
import { CategoryIcon } from "../../components/CategoryIcon";
import { AUTO_PALETTE } from "../../constants/palettes";
import { DurationRingPicker } from "../../components/DurationRingPicker";
import {
  appBlockerIsNative, setSessionBlocking, getBlockedPackages,
  isUsageAccessEnabled, canDrawOverlays, BLOCKLIST_CHANGED_EVENT,
} from "../../capacitor/appBlocker";
import { appBlockingState } from "../../utils/appBlockingState";
import { showTimerNotification, hideTimerNotification, consumePendingTimerToggle } from "../../capacitor/timerNotification";
import { scheduleTimerAlarm, cancelTimerAlarm, timerAlarmIsNative } from "../../capacitor/timerAlarm";
import { enableKeepAwake, disableKeepAwake } from "../../capacitor/keepAwake";
import { auth } from "../../firebase/config";
import { usePremium } from "../../firebase/billing";
import { syncClockInSession, clearClockInSession } from "../../firebase/clockInSession";

// Assign-to-do bottom sheet motion: the same as Habits > Manage Habits, which
// Jade found smooth (2026-10-16): the dim is just there (no fade in), the sheet
// slides up + fades in at once (no hold, no will-change), and nothing inside it
// animates while it slides. The list only slides in from the side after a TAB
// SWITCH; on open it used to slide in from the right at the same moment as the
// sheet, two animations stacked, which is what lagged on the phone.
// Added to <head> once at load, like SessionSheet's CSS, so opening the panel
// doesn't restyle the page.
const QUEUE_SHEET_CSS = `
@keyframes cqSheetUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
@keyframes cqTabInR { from { opacity: 0; transform: translateX(22px); } to { opacity: 1; transform: none; } }
@keyframes cqTabInL { from { opacity: 0; transform: translateX(-22px); } to { opacity: 1; transform: none; } }
.cq-sheet { animation: cqSheetUp 0.32s cubic-bezier(0.22,1,0.36,1) both; }
.cq-tab-r { animation: cqTabInR 0.24s cubic-bezier(0.22,1,0.36,1) both; }
.cq-tab-l { animation: cqTabInL 0.24s cubic-bezier(0.22,1,0.36,1) both; }
@media (prefers-reduced-motion: reduce) { .cq-sheet, .cq-tab-r, .cq-tab-l { animation: none; } }
/* The habit / task list scrolls with no scrollbar (Jade, 2026-10-21). Needs
   !important to beat the app-wide ::-webkit-scrollbar rule. */
.cq-list { scrollbar-width: none !important; }
.cq-list::-webkit-scrollbar { width: 0 !important; height: 0 !important; display: none !important; }
`;
if (typeof document !== "undefined" && !document.getElementById("cq-sheet-css")) {
  const el = document.createElement("style");
  el.id = "cq-sheet-css";
  el.textContent = QUEUE_SHEET_CSS;
  document.head.appendChild(el);
}
// The sheet's tinted surface and its cards (no borders; a soft shadow instead).
const CQ_SHEET_BG = "color-mix(in srgb, var(--c-primary) 9%, var(--c-bg-card))";
const CQ_CARD_SHADOW = "0 2px 8px rgba(60,30,50,0.08), 0 1px 2px rgba(60,30,50,0.05)";
// A picked card: the accent at a stronger pastel (Jade, 2026-10-15; was 16%).
const CQ_PICKED_BG = "color-mix(in srgb, var(--c-primary) 30%, var(--c-bg-card))";
const CQ_TABS = ["habits", "tasks", "categories"];
// The timer's own height on the page: the space between the app's top bar and
// the tab bar, so the group sits in the middle of the screen (Jade, 2026-10-24).
// 250 = the top bar + the tab content's padding: exactly what keeps the page from
// scrolling (230 left it 18px too tall; Jade wants the timer page fixed).
const CI_STAGE_MIN_H = "calc(100vh - var(--sat, 0px) - 250px)";
// The timer ring has one size whether it's the duration picker (before Start) or
// counting down (Jade: it shrank on Start). Same numbers DurationRingPicker uses:
// ring diameter, stroke, and the canvas padded for the picker's drag dot.
const CI_RING = 236, CI_RING_STROKE = 10;
// Height kept for the controls under the ring: Block apps + Start + Add past session,
// the tallest set (see .ci-controls).
const CI_CONTROLS_H = 104;
const CI_RING_R = (CI_RING - CI_RING_STROKE) / 2;
const CI_RING_CANVAS = CI_RING + 2 * (CI_RING_STROKE * 0.72 + 2.5 + 2);
// Plain icon button for the timer page's top bar (Select, Chart, ⋯): no frosted
// background (Jade, 2026-10-24), same as the Chart page's top bar.
const ciRoundBtn = (ci) => ({ width: 40, height: 40, minHeight: 0, minWidth: 0, borderRadius: "50%", padding: 0, border: "none", background: "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: ci.ink, flexShrink: 0 });
// Fuller stats icon than lucide's thin bars, so it reads as a button (Jade, 2026-10-24).
const StatsBarsIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="3" y="12" width="5" height="9" rx="1.6" /><rect x="9.5" y="4" width="5" height="17" rx="1.6" /><rect x="16" y="8" width="5" height="13" rx="1.6" />
  </svg>
);

export function FocusTab({ focusSessions, setFocusSessions, focusTimerState, setFocusTimerState, habits = [], setHabits = () => {}, habitLogs = {}, setHabitLogs = () => {}, habitSkips = [], setHabitSkips = () => {}, tasks = [], setTasks = () => {}, taskOccurrenceOverrides = {}, setTaskOccurrenceOverrides = () => {}, categories = [], setCategories = null, showBackToTop = false, isDesktop = false, onEditTask = () => {}, onEditHabit = () => {}, ProfileTab, onOpenAppBlockerSettings = () => {}, weekStart = "sunday", topBarSlotNode = null, wide = false }) {
  // Clock-in analytics sub-page, opened via the Statistics button beside the
  // mode selector.
  const [showStats, setShowStats] = useState(false);
  // The timer page is fixed, only the Chart page scrolls (Jade, 2026-10-25): see
  // html[data-ci-timer] in App.jsx.
  useLayoutEffect(() => {
    const root = document.documentElement;
    // Desktop: the Chart page is fixed too; only its logged sessions list scrolls (Jade, 2026-11-02).
    if (showStats && !wide) { delete root.dataset.ciTimer; return undefined; }
    root.dataset.ciTimer = "";
    const main = document.querySelector(".clarity-main");
    if (main) main.scrollTop = 0;
    return () => { delete root.dataset.ciTimer; };
  }, [showStats, wide]);
  const [statsViewSlot, setStatsViewSlot] = useState(null); // DOM node beside the menu button that the donut page portals its Week / Month / Year dropdown into
  const [statsToolbarSlot, setStatsToolbarSlot] = useState(null); // DOM node beside the Back-to-timer button that the donut page portals its meatballs menu into
  const [ciMenu, setCiMenu] = useState(null); // { top, right } of the open ⋯ menu (timer page top bar)
  // ── sub-tab: "classic" | "custom" | "stopwatch"
  const [mode, setMode] = useState(focusTimerState?.mode ?? load("clarity_focusMode", "classic"));

  // ── Timer state
  const [running, setRunning] = useState(focusTimerState?.running ?? false);
  // Ring-drag duration picker always carries a concrete number (5-200min) — no
  // more blank/"optional" text state. Break support was removed for now.
  const [customWork, setCustomWork] = useState(focusTimerState?.customWork ?? load("clarity_focusCustomWork", 25));
  const [sessions, setSessions] = useState(focusTimerState?.sessions ?? 0);
  const [timeLeft, setTimeLeft] = useState(focusTimerState?.timeLeft ?? 25 * 60);
  const [paused, setPaused] = useState(focusTimerState?.paused ?? false);

  // ── Stopwatch state
  const [swRunning, setSwRunning] = useState(focusTimerState?.swRunning ?? false);
  const [swElapsed, setSwElapsed] = useState(focusTimerState?.swElapsed ?? 0);  // ms
  const [swPaused, setSwPaused] = useState(focusTimerState?.swPaused ?? false);

  // ── Habit assignment (shared across modes)
  const [assignedItem, setAssignedItem] = useState(focusTimerState?.assignedItem ?? null); // {label, emoji} or null
  const [showHabitPicker, setShowHabitPicker] = useState(false);
  const [headerLabel, setHeaderLabel] = useState(focusTimerState?.headerLabel ?? "");

  // Task category picker state

  // ── UI state
  // Hiding the running duration only ever made sense for the stopwatch (you
  // set a timer BECAUSE you want to see the countdown) — this toggle lives
  // entirely here now (via the slide-out drawer in Elapsed mode), not in
  // Settings, and only ever applies to the stopwatch.
  const [hideStopwatchTime, setHideStopwatchTime] = useState(() => load("clarity_focusHideStopwatchTime", false));
  const toggleHideStopwatchTime = () => {
    const next = !hideStopwatchTime;
    setHideStopwatchTime(next);
    save("clarity_focusHideStopwatchTime", next);
  };
  // Same drawer setting the donut/logged-sessions page reads — also hides the
  // duration/time-log breakdown inside the shared details panel.
  const [hideSessionDuration, setHideSessionDuration] = useState(() => load("clarity_focusHideDuration", false));
  useEffect(() => {
    const onChange = () => setHideSessionDuration(load("clarity_focusHideDuration", false));
    window.addEventListener("clarity-hide-session-duration", onChange);
    return () => window.removeEventListener("clarity-hide-session-duration", onChange);
  }, []);
  const [showFinishModal, setShowFinishModal] = useState(false);
  // Optional inline "Sub-tasks" list under the active task's label on the main
  // timer/stopwatch card — collapsed by default, toggled via a dropdown arrow.
  const [showInlineSubtasks, setShowInlineSubtasks] = useState(false);

  const intervalRef = useRef(null);
  const startTimestampRef = useRef(focusTimerState?.startTimestamp ?? null);
  const totalDurationRef = useRef(focusTimerState?.totalDuration ?? null);
  const pausedAtRef = useRef(focusTimerState?.pausedAt ?? null);
  // Remaining ms frozen at the moment of pause. Persisted so a paused timer
  // survives a full app reload (see handleStartPause / mount restore).
  const pausedRemainingRef = useRef(focusTimerState?.pausedRemaining ?? null);
  const wakeLockRef = useRef(null);
  const hasMountedRef = useRef(false);

  // Stopwatch refs for background-safe timing
  const swStartRef = useRef(focusTimerState?.swStart ?? null);
  const swAccRef = useRef(focusTimerState?.swAcc ?? 0); // accumulated ms before last start
  const swPausedAtRef = useRef(focusTimerState?.swPausedAt ?? null);

  // Sync all state to parent so it survives tab switches
  // (spread prev so the queue/runner fields written by the queue effect below survive)
  useEffect(() => {
    setFocusTimerState(prev => ({
      ...prev,
      mode, running, customWork, sessions, timeLeft, paused,
      startTimestamp: startTimestampRef.current,
      totalDuration: totalDurationRef.current,
      pausedAt: pausedAtRef.current,
      pausedRemaining: pausedRemainingRef.current,
      swRunning, swElapsed, swPaused,
      swStart: swStartRef.current,
      swAcc: swAccRef.current,
      swPausedAt: swPausedAtRef.current,
      assignedItem, headerLabel,
    }));
  }, [mode, running, customWork, sessions, timeLeft, paused, swRunning, swElapsed, swPaused, assignedItem, headerLabel]); // eslint-disable-line

  const workSecs = mode === "classic" ? 25 * 60 : (Number(customWork) || 25) * 60;
  const workMins = mode === "classic" ? 25 : (Number(customWork) || 25);

  // ── Timer helpers
  const computeRemaining = () => {
    const start = startTimestampRef.current || load("clarity_focusStart", null);
    const dur = totalDurationRef.current || load("clarity_focusDuration", null);
    if (start == null || dur == null) return null;
    return Math.max(0, Math.ceil((dur - (Date.now() - start)) / 1000));
  };

  const handleSessionComplete = (currentWorkMins) => {
    // On native Android, the countdown's own scheduled alarm (see the
    // scheduleTimerAlarm effect below) already rings — reliably, even if the
    // app was backgrounded when this fired — so playing this one-shot <audio>
    // too would double up the sound. Web/non-native builds have no such
    // alarm, so this is their only completion sound.
    if (!timerAlarmIsNative) { try { new Audio(TIMER_ALARM_SOUND_FILE).play().catch(() => {}); } catch {} }
    cancelTimerAlarm();
    clearInterval(intervalRef.current);
    setRunning(false); setPaused(false);
    disarmAppBlocking();
    startTimestampRef.current = null; totalDurationRef.current = null; pausedRemainingRef.current = null;
    localStorage.removeItem("clarity_focusStart"); localStorage.removeItem("clarity_focusDuration");
    localStorage.removeItem("clarity_focusPaused");
    localStorage.removeItem("clarity_focusRemaining");
    // A Timer running out on its own, while a plan item is active, finishes
    // THAT item — same as tapping the check button — instead of logging a
    // generic standalone session. Frozen at 00:00 remaining until the user
    // restarts it.
    if (plan && planCurrent && !plan.doneCurrent) {
      // Ran the full duration → snapshot the full elapsed and mark it
      // auto-completed so the restart panel only offers a full reset (there's
      // nothing left to "continue").
      completeCurrentPlanItem(currentWorkMins, workSecs * 1000, true);
      return;
    }
    setSessions(s => s + 1);
    setFocusSessions(p => [...p, { date: fmt(new Date(Date.now() - currentWorkMins * 60000)), minutes: currentWorkMins, completedAt: new Date().toISOString(), startedAt: new Date(Date.now() - currentWorkMins * 60000).toISOString(), label: sessionNote?.trim() || headerLabel || null, categoryLabel: assignedItem?.label || null, categoryId: assignedItem?.categoryId || null }]);
    sonnerToast(`Clocked in! +${currentWorkMins} min`);
    setShowContinue(true);
    // Fire bridge event so donut chart pulses the right slice
    window.dispatchEvent(new CustomEvent("clarity:sessionComplete", {
      detail: { categoryLabel: assignedItem?.categoryLabel || null, label: assignedItem?.label || null }
    }));
    setTimeLeft(workSecs);
  };

  const startTicking = (currentWorkMins) => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      const remaining = computeRemaining();
      if (remaining === null) return;
      if (remaining <= 0) { setTimeLeft(0); handleSessionComplete(currentWorkMins); return; }
      setTimeLeft(remaining);
    }, 500);
  };

  useEffect(() => {
    const syncTimer = () => {
      if (document.visibilityState === "visible" && running && !paused) {
        const remaining = computeRemaining();
        if (remaining !== null) setTimeLeft(Math.max(0, remaining));
      }
      if (document.visibilityState === "visible" && swRunning && !swPaused && swStartRef.current) {
        const elapsed = swAccRef.current + (Date.now() - swStartRef.current);
        setSwElapsed(elapsed);
      }
    };
    document.addEventListener("visibilitychange", syncTimer);
    return () => document.removeEventListener("visibilitychange", syncTimer);
  }, [running, paused, swRunning, swPaused]); // eslint-disable-line

  // On mount: restore timer
  useEffect(() => {
    if (running && !paused && startTimestampRef.current && totalDurationRef.current) {
      const remaining = computeRemaining();
      if (remaining > 0) { setTimeLeft(remaining); startTicking(workMins); }
      else handleSessionComplete(workMins);
    } else if (!running) {
      const savedStart = load("clarity_focusStart", null);
      const savedDur = load("clarity_focusDuration", null);
      const savedPaused = load("clarity_focusPaused", "0") === "1";
      const savedWorkMins = load("clarity_focusWorkMins", workMins);
      if (savedStart != null && savedDur != null) {
        startTimestampRef.current = savedStart; totalDurationRef.current = savedDur;
        if (savedPaused) {
          // Paused before the reload: restore the frozen remaining rather than
          // recomputing from wall-clock (which would keep counting down while
          // paused and, on resume, blow up the timer).
          const savedRemaining = load("clarity_focusRemaining", null);
          const remainingMs = savedRemaining != null
            ? savedRemaining
            : Math.max(0, savedDur - (Date.now() - savedStart));
          if (remainingMs > 0) {
            pausedRemainingRef.current = remainingMs;
            setTimeLeft(Math.ceil(remainingMs / 1000));
            setPaused(true); setRunning(true);
          } else {
            localStorage.removeItem("clarity_focusStart"); localStorage.removeItem("clarity_focusDuration");
            localStorage.removeItem("clarity_focusPaused");
            localStorage.removeItem("clarity_focusRemaining");
            disarmAppBlocking();
          }
        } else {
          const remaining = computeRemaining();
          if (remaining > 0) {
            setTimeLeft(remaining); setPaused(false); setRunning(true);
            startTicking(savedWorkMins);
          } else {
            localStorage.removeItem("clarity_focusStart"); localStorage.removeItem("clarity_focusDuration");
            localStorage.removeItem("clarity_focusPaused");
            localStorage.removeItem("clarity_focusRemaining");
            disarmAppBlocking();
          }
        }
      }
    }
    // Restore stopwatch. Tab switches seed swRunning/swPaused + refs from
    // focusTimerState; a full reload loses those, so fall back to localStorage
    // so a running OR paused elapsed session resumes where it left off (instead
    // of snapping back to 00:00).
    if (swRunning) {
      if (!swPaused && swStartRef.current) {
        setSwElapsed(swAccRef.current + (Date.now() - swStartRef.current));
        const swRef = setInterval(() => {
          if (swStartRef.current) setSwElapsed(swAccRef.current + (Date.now() - swStartRef.current));
        }, 100);
        intervalRef.current = swRef; // share ref when in stopwatch mode
      }
    } else if (load("clarity_swRunning", false)) {
      const savedAcc = load("clarity_swAcc", 0);
      const savedStart = load("clarity_swStart", null);
      const savedPaused = load("clarity_swPaused", false);
      swAccRef.current = savedAcc;
      if (savedPaused || savedStart == null) {
        // Paused before the reload: elapsed is frozen at the accumulated total.
        swStartRef.current = null;
        setSwElapsed(savedAcc);
        setSwRunning(true); setSwPaused(true);
      } else {
        // Running: keep counting from the saved start timestamp.
        swStartRef.current = savedStart;
        setSwElapsed(savedAcc + (Date.now() - savedStart));
        setSwRunning(true); setSwPaused(false);
        const swRef = setInterval(() => {
          if (swStartRef.current) setSwElapsed(swAccRef.current + (Date.now() - swStartRef.current));
        }, 100);
        intervalRef.current = swRef;
      }
    }
    hasMountedRef.current = true;
    return () => clearInterval(intervalRef.current);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!hasMountedRef.current) return;
    if (mode === "stopwatch") {
      clearInterval(intervalRef.current);
      if (swRunning && !swPaused) {
        intervalRef.current = setInterval(() => {
          if (swStartRef.current) setSwElapsed(swAccRef.current + (Date.now() - swStartRef.current));
        }, 100);
      }
    } else {
      if (running && !paused) startTicking(workMins);
      else clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [running, paused, swRunning, swPaused, mode]); // eslint-disable-line

  // App Blocker: whether THIS session should cover the picked apps at all —
  // a per-session choice, not a global setting. Defaults to on (remembers the
  // last choice via localStorage) so the common case still needs zero taps,
  // but flipping it off before Start lets you keep e.g. Maps/Slack usable for
  // a session where you'll genuinely need them. Locked (can't be changed)
  // once the session is running — see the toggle row's `disabled` below —
  // exactly like the blocklist picker itself locks in Settings, so the
  // decision has to be made before Start, not mid-session.
  const [blockAppsThisSession, setBlockAppsThisSession] = useState(() => load("clarity_blockAppsThisSession", true));
  const toggleBlockAppsThisSession = () => {
    const active = mode === "stopwatch" ? swRunning : running;
    if (active) return; // locked once running
    setBlockAppsThisSession(prev => {
      const next = !prev;
      save("clarity_blockAppsThisSession", next);
      return next;
    });
  };

  // Only worth showing the toggle at all if App Blocker is actually usable —
  // permissions granted AND at least one app picked in Settings. Otherwise
  // there'd be nothing for the switch to meaningfully turn on/off.
  // App Blocker is Premium: a blocklist saved on this phone (it lives in the
  // native prefs, not the account) must not let a free account block apps.
  // Checked when a session is armed (Start/Resume), never mid-session — a
  // running session keeps its block even if Premium lapses meanwhile.
  const { isPremium } = usePremium();
  const [appBlockerSetUp, setAppBlockerReady] = useState(false);
  const appBlockerReady = appBlockerSetUp && isPremium;
  useEffect(() => {
    if (!appBlockerIsNative) return;
    let cancelled = false;
    const refresh = () => {
      Promise.all([isUsageAccessEnabled(), canDrawOverlays(), getBlockedPackages()])
        .then(([usageAccessOn, overlayOn, blocked]) => {
          if (!cancelled) setAppBlockerReady(usageAccessOn && overlayOn && blocked.length > 0);
        })
        .catch(() => { if (!cancelled) setAppBlockerReady(false); });
    };
    refresh();
    document.addEventListener("visibilitychange", refresh); // permissions granted in Android's Settings
    window.addEventListener(BLOCKLIST_CHANGED_EVENT, refresh); // apps picked in Clarity's Settings, opened over this tab
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener(BLOCKLIST_CHANGED_EVENT, refresh);
    };
  }, []);

  // Whether this session actually blocks — see utils/appBlockingState.js.
  // Persisted so a blocking session keeps blocking after the app is killed
  // and reopened, but under a non-`clarity_` key: sync.js mirrors every
  // clarity_* key to other devices, and this describes THIS phone only.
  const [appBlockArmed, setAppBlockArmed] = useState(() => load("clarityAppBlockArmed", false));
  const setArmed = (next) => { setAppBlockArmed(next); save("clarityAppBlockArmed", next); };
  // Start decides afresh, so a leftover armed flag can't outlive an unticked Start.
  const armAppBlockingOnStart = () => setArmed(blockAppsThisSession && appBlockerReady);
  // Resume can arm but never disarm — resuming must not lift a block any more than pausing does.
  const armAppBlockingOnResume = () => { if (!appBlockArmed && blockAppsThisSession && appBlockerReady) setArmed(true); };
  const disarmAppBlocking = () => setArmed(false);

  const handleStartPause = () => {
    // Custom mode needs a focus duration before it can start (no hidden default).
    if (!running && mode === "custom" && (customWork === "" || Number(customWork) < 1)) return;
    if (!running) {
      const dur = workSecs * 1000;
      startTimestampRef.current = Date.now(); totalDurationRef.current = dur;
      save("clarity_focusStart", startTimestampRef.current);
      save("clarity_focusDuration", totalDurationRef.current);
      save("clarity_focusWorkMins", workMins); save("clarity_focusPaused", "0");
      setRunning(true); setPaused(false);
      armAppBlockingOnStart();
      openAmountOnStart();
    } else if (!paused) {
      // Pause: freeze the remaining time and persist it, so the paused state
      // survives a full app reload. We no longer rely on an in-memory pausedAt
      // for resume math (that produced a garbage jump after a reload).
      const start = startTimestampRef.current || load("clarity_focusStart", null);
      const dur = totalDurationRef.current || load("clarity_focusDuration", null);
      const remainingMs = Math.max(0, (dur ?? 0) - (Date.now() - (start ?? Date.now())));
      pausedAtRef.current = Date.now();
      pausedRemainingRef.current = remainingMs;
      save("clarity_focusRemaining", remainingMs);
      save("clarity_focusPaused", "1");
      setTimeLeft(Math.ceil(remainingMs / 1000));
      setPaused(true);
    } else {
      // Resume: rebuild the start timestamp from the frozen remaining, so the
      // countdown picks up exactly where it left off regardless of how long the
      // app was closed.
      const dur = totalDurationRef.current || load("clarity_focusDuration", null);
      const remainingMs = pausedRemainingRef.current ?? load("clarity_focusRemaining", null) ?? dur ?? 0;
      startTimestampRef.current = Date.now() - ((dur ?? 0) - remainingMs);
      totalDurationRef.current = dur;
      pausedRemainingRef.current = null;
      save("clarity_focusStart", startTimestampRef.current);
      save("clarity_focusPaused", "0");
      localStorage.removeItem("clarity_focusRemaining");
      setPaused(false);
      armAppBlockingOnResume();
    }
  };

  const handleReset = (explicitWorkSecs) => {
    clearInterval(intervalRef.current);
    setRunning(false); setPaused(false);
    disarmAppBlocking();
    startTimestampRef.current = null; totalDurationRef.current = null; pausedRemainingRef.current = null;
    localStorage.removeItem("clarity_focusStart"); localStorage.removeItem("clarity_focusDuration");
    localStorage.removeItem("clarity_focusPaused"); localStorage.removeItem("clarity_focusWorkMins");
    localStorage.removeItem("clarity_focusRemaining");
    const safeSecs = explicitWorkSecs ?? workSecs;
    setTimeLeft(isNaN(safeSecs) || safeSecs < 1 ? 25 * 60 : safeSecs);
  };

  // ── Stopwatch controls
  // Persist the stopwatch to localStorage (like the classic timer) so a paused
  // or running elapsed session survives a full app reload, not just tab
  // switches. clarity_swStart is null while paused; elapsed is then frozen at
  // clarity_swAcc.
  const saveSwState = () => {
    save("clarity_swRunning", true);
    save("clarity_swPaused", swPausedRef());
    save("clarity_swAcc", swAccRef.current);
    if (swStartRef.current != null) save("clarity_swStart", swStartRef.current);
    else localStorage.removeItem("clarity_swStart");
  };
  const swPausedRef = () => swStartRef.current == null;
  const clearSwState = () => {
    localStorage.removeItem("clarity_swRunning");
    localStorage.removeItem("clarity_swPaused");
    localStorage.removeItem("clarity_swStart");
    localStorage.removeItem("clarity_swAcc");
  };
  const handleSwStartPause = () => {
    if (!swRunning) {
      swStartRef.current = Date.now();
      swAccRef.current = swElapsed;
      setSwRunning(true); setSwPaused(false);
      saveSwState();
      armAppBlockingOnStart();
      openAmountOnStart();
    } else if (!swPaused) {
      swPausedAtRef.current = Date.now();
      swAccRef.current = swAccRef.current + (Date.now() - swStartRef.current);
      swStartRef.current = null;
      setSwPaused(true);
      // Snap the displayed time to the exact frozen value right away — the
      // ticking interval (100ms, and throttled further when backgrounded)
      // can leave the on-screen number several seconds stale otherwise,
      // which then "jumps forward" to the true value after a reload.
      setSwElapsed(swAccRef.current);
      saveSwState();
    } else {
      swStartRef.current = Date.now();
      setSwPaused(false);
      saveSwState();
      armAppBlockingOnResume();
    }
  };

  const handleSwReset = () => {
    clearInterval(intervalRef.current);
    setSwRunning(false); setSwPaused(false); setSwElapsed(0);
    disarmAppBlocking();
    swStartRef.current = null; swAccRef.current = 0; swPausedAtRef.current = null;
    clearSwState();
  };

  // Settings ▸ reset "Clock-In" fires this: besides wiping the donut/sessions
  // (handled in App), the in-progress plan should restart to its first item
  // (e.g. "To-Do 1/10") with a fresh timer instead of staying mid-way.
  useEffect(() => {
    const onReset = () => {
      setPlanReview(null);
      setPlan(p => p ? { ...p, index: 0, doneCurrent: false, lastLog: null, items: p.items.map(({ savedElapsedMs, ...rest }) => rest) } : p);
      if (mode === "stopwatch") handleSwReset(); else handleReset();
    };
    window.addEventListener("clarity-clockin-reset", onReset);
    return () => window.removeEventListener("clarity-clockin-reset", onReset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Put the countdown timer into a paused state showing an exact remaining
  // value, mirroring handleStartPause's own pause branch. Used to restore a
  // plan item's saved position when navigating back to it.
  const restoreTimerPaused = (remainingMs, totalMs) => {
    startTimestampRef.current = Date.now() - (totalMs - remainingMs);
    totalDurationRef.current = totalMs;
    pausedRemainingRef.current = remainingMs;
    save("clarity_focusStart", startTimestampRef.current);
    save("clarity_focusDuration", totalDurationRef.current);
    save("clarity_focusWorkMins", workMins); save("clarity_focusPaused", "1");
    save("clarity_focusRemaining", remainingMs);
    setTimeLeft(Math.ceil(remainingMs / 1000));
    setRunning(true); setPaused(true);
  };

  // Stopwatch equivalent — freezes swElapsed at a specific value, paused.
  const restoreStopwatchPaused = (elapsedMs) => {
    swAccRef.current = elapsedMs;
    swStartRef.current = null;
    setSwElapsed(elapsedMs);
    setSwRunning(true); setSwPaused(true);
    saveSwState();
  };

  // Exact elapsed ms of the live session, for snapshotting onto a plan item so
  // navigating back to it can restore its precise position (down to the second),
  // not a minute-rounded approximation.
  const currentElapsedMs = () => {
    if (mode === "stopwatch") return Math.max(0, swElapsed);
    const totalMs = workSecs * 1000;
    let remainingMs;
    if (pausedRemainingRef.current != null) remainingMs = pausedRemainingRef.current;
    else if (startTimestampRef.current != null && totalDurationRef.current != null) {
      remainingMs = Math.max(0, totalDurationRef.current - (Date.now() - startTimestampRef.current));
    } else {
      remainingMs = (typeof timeLeft === "number" ? timeLeft : workSecs) * 1000;
    }
    return Math.max(0, totalMs - remainingMs);
  };

  const handleSwLog = () => {
    const mins = Math.round(swElapsed / 60000);
    setFocusSessions(p => [...p, { date: fmt(new Date(Date.now() - swElapsed)), minutes: mins, completedAt: new Date().toISOString(), startedAt: new Date(Date.now() - swElapsed).toISOString(), label: sessionNote?.trim() || headerLabel || null, categoryLabel: assignedItem?.label || null, categoryId: assignedItem?.categoryId || null }]);
    sonnerToast(`Logged! +${mins} min`);
    window.dispatchEvent(new CustomEvent("clarity:sessionComplete", {
      detail: { categoryLabel: assignedItem?.categoryLabel || null, label: assignedItem?.label || null }
    }));
    handleSwReset();
  };

  const hasModeChangedRef = useRef(false);
  useEffect(() => {
    if (!hasModeChangedRef.current) { hasModeChangedRef.current = true; return; }
    handleReset(); handleSwReset();
  }, [mode]); // eslint-disable-line

  // ── Deep Mode
  useEffect(() => { return () => { if (wakeLockRef.current) { wakeLockRef.current.release(); wakeLockRef.current = null; } }; }, []);

  // ── Computed display
  // If in custom mode and the field is empty, show blank preview
  const customEmpty = mode === "custom" && (customWork === "" || customWork < 1);
  const safeTimeLeft = isNaN(timeLeft) || timeLeft < 0 ? 0 : timeLeft;
  const mins = customEmpty ? "--" : String(Math.floor(safeTimeLeft / 60)).padStart(2, "0");
  const secs = customEmpty ? "--" : String(safeTimeLeft % 60).padStart(2, "0");
  const pct = customEmpty ? 0 : (safeTimeLeft > 0 ? 1 - safeTimeLeft / workSecs : 1);

  // Custom mode's ring is never hidden — it's the duration picker, and once
  // running you're supposed to see the countdown you set. Only the stopwatch
  // can be hidden, via its own drawer toggle (hideStopwatchTime).
  const showRing = mode === "stopwatch" ? !hideStopwatchTime : true;

  // Stopwatch display
  const swTotalMs = swElapsed;
  const swH = Math.floor(swTotalMs / 3600000);
  const swM = Math.floor((swTotalMs % 3600000) / 60000);
  const swS = Math.floor((swTotalMs % 60000) / 1000);
  const swMs = Math.floor((swTotalMs % 1000) / 10);
  const swDisplay = swH > 0
    ? `${String(swH).padStart(2,"0")}:${String(swM).padStart(2,"0")}:${String(swS).padStart(2,"0")}`
    : `${String(swM).padStart(2,"0")}:${String(swS).padStart(2,"0")}.${String(swMs).padStart(2,"0")}`;

  // Habit picker items: all habits + all incomplete tasks
  const todayDateStr = todayStr();
  const todayDateObj = new Date(todayDateStr + "T00:00:00");
  const todayDow = todayDateObj.getDay();
  const todayDayNum = todayDateObj.getDate();



  const clearAssignment = () => {
    setAssignedItem(null);
    setHeaderLabel("");
    setCustomTaskInput("");
    setPendingTaskItem(null);
    setTaskCategoryInput("");
  };

  // Dynamic header label
  const timerHeader = headerLabel || (mode === "classic" ? "Work" : mode === "custom" ? "Work" : "Stopwatch");

  const handleFinish = () => {
    const elapsed = workSecs - timeLeft;
    const mins = Math.round(elapsed / 60);
    setFocusSessions(p => [...p, { date: fmt(new Date(Date.now() - elapsed * 1000)), minutes: mins, completedAt: new Date().toISOString(), startedAt: new Date(Date.now() - elapsed * 1000).toISOString(), label: sessionNote?.trim() || headerLabel || null, categoryLabel: assignedItem?.label || null, categoryId: assignedItem?.categoryId || null }]);
    setSessions(s => s + 1);
    sonnerToast(`Logged! +${mins} min`);
    window.dispatchEvent(new CustomEvent("clarity:sessionComplete", {
      detail: { categoryLabel: assignedItem?.categoryLabel || null, label: assignedItem?.label || null }
    }));
    handleReset();
    setShowFinishModal(false);
  };

  const handleSwFinish = () => {
    const mins = Math.round(swElapsed / 60000);
    setFocusSessions(p => [...p, { date: fmt(new Date(Date.now() - swElapsed)), minutes: mins, completedAt: new Date().toISOString(), startedAt: new Date(Date.now() - swElapsed).toISOString(), label: sessionNote?.trim() || headerLabel || null, categoryLabel: assignedItem?.label || null, categoryId: assignedItem?.categoryId || null }]);
    sonnerToast(`Logged! +${mins} min`);
    window.dispatchEvent(new CustomEvent("clarity:sessionComplete", {
      detail: { categoryLabel: assignedItem?.categoryLabel || null, label: assignedItem?.label || null }
    }));
    handleSwReset();
    setShowFinishModal(false);
  };

  const FinishModal = () => {
    const elapsedMins = mode === "stopwatch"
      ? Math.round(swElapsed / 60000)
      : Math.round((workSecs - timeLeft) / 60);
    return (
      <div style={{ ...CONFIRM_OVERLAY, zIndex: 10050 }}>
        <div className="clarity-fade" style={CONFIRM_DIM} onClick={() => setShowFinishModal(false)} />
        <div className="clarity-pop" style={CONFIRM_CARD}>
          <ConfirmHeader tone="accent" icon={CheckCircle} title="Finish clock-in?" subtitle={`${elapsedMins} min will be logged.`} />
          <div style={CONFIRM_STACK}>
            <button onClick={() => setShowFinishModal(false)} style={CONFIRM_CANCEL}>Cancel</button>
            <button onClick={mode === "stopwatch" ? handleSwFinish : handleFinish} style={CONFIRM_PRIMARY}>Finish</button>
          </div>
        </div>
      </div>
    );
  };

  const canFinish = mode === "stopwatch" ? (swRunning || swPaused) : running;

  // ══ Habit/Task Queue + Runner state (Clock-In "do my day" flow) ══════════════
  const [showQueuePanel, setShowQueuePanel] = useState(false);
  // Swipe the assign sheet down (it follows the finger) or tap the dim: it slides
  // down, then closes (useSheetDrag, Jade 2026-10-22).
  const queueSheet = useSheetDrag(() => setShowQueuePanel(false));
  const queueTabDirRef = useRef(0); // 1 = moved right (list slides in from the right), -1 = left, 0 = just opened (no slide)
  // Add amount sheet for a unit habit other than Time: opened when its session
  // starts (Jade, 2026-10-22) and again after it, to log the amount.
  const [focusAmountSheetId, setFocusAmountSheetId] = useState(null);
  const [focusAmountAtStart, setFocusAmountAtStart] = useState(false);
  // Start (not Resume) on a unit habit slides its Add amount sheet up, the same
  // sheet as the Habits tab's "+ 4.5k/10k" button. Time habits are left out: the
  // session itself adds their minutes.
  const openAmountOnStart = () => {
    if (!plan || plan.doneCurrent || !planCurrent || planCurrent.kind !== "habit") return;
    const h = habits.find(x => x.id === planCurrent.id);
    if (!h || !isAmountHabit(h) || unitOf(h) === "time") return;
    setFocusAmountAtStart(true);
    setFocusAmountSheetId(h.id);
  };
  // Saved drag order for today's plan. Persisted to localStorage (not just the
  // in-memory focusTimerState) so the arrangement survives an app close, a reload,
  // or a mobile browser discarding the tab on app-switch. The qids are stable
  // (h:id / t:id / c:id), so the order sticks until the user re-drags the cards.
  const [queueOrder, setQueueOrder]   = useState(focusTimerState?.queueOrder ?? load("clarity_focusQueueOrder", null));   // array of queue-ids, or null = default order
  // Single-pick only — clamp to the first entry so old saved state from before
  // the multi-select queue was retired (which could hold many ids) doesn't
  // make every card show up pre-checked.
  const [queueSelected, setQueueSelected] = useState(() => new Set([...(focusTimerState?.queueSelected ?? [])].slice(0, 1)));
  // queueCanceled / customTodos / promotedCounted: same durability need as
  // queueOrder above — these three also define what's actually in today's plan,
  // so they're seeded from localStorage (not just in-memory focusTimerState) and
  // kept in sync below, or a stale-tab close would silently undo them too.
  // queueCanceled is "removed/skipped for TODAY" — date-scoped so a recurring
  // habit/task removed today comes back on its next scheduled day instead of
  // staying hidden forever once it's durable.
  const [queueCanceled, setQueueCanceled] = useState(() => {
    if (focusTimerState?.queueCanceled) return focusTimerState.queueCanceled;
    const stored = load("clarity_focusQueueCanceled", null);
    return (stored && stored.date === todayStr()) ? stored.items : [];
  }); // [{ qid, reason: "removed"|"skipped" }]
  // [{ id, title, description, goalId, emoji, frequencyType, days, dayLabels, weeklyCount, monthlyMode, monthDates, monthlyCount }]
  // frequencyType mirrors the habit frequency picker (daily/specific/count/monthly),
  // minus yearly — to-dos are short-lived, unlike habits. Missing/"daily" both mean
  // "always due", so old plain to-dos (saved before this field existed) keep behaving
  // exactly as before via isHabitDueOn's default true.
  const [customTodos, setCustomTodos] = useState(focusTimerState?.customTodos ?? load("clarity_focusCustomTodos", []));
  // Single modal handles both creating and editing a custom to-do (title, optional
  // description/emoji/category, and its recurrence) — null when closed, else a draft
  // object being edited. draft.id is null for a brand-new to-do, else the existing id.
  const [customTodoEditor, setCustomTodoEditor] = useState(null);
  const [showDeleteTodoConfirm, setShowDeleteTodoConfirm] = useState(false); // confirm gate before deleteCustomTodoFromEditor actually deletes
  const [expandedQueueId, setExpandedQueueId] = useState(null);
  const [showDoneSection, setShowDoneSection] = useState(false);
  // Same completed-today list as the queue panel's Done section, but rendered
  // directly on the main clock-in card so it's visible all day without having
  // to open the panel — expanded by default, survives reload since it's derived
  // straight from the persisted habits/tasks/habitLogs state.
  const [showCountedSection, setShowCountedSection] = useState(true);
  // Counted habits dragged up into today's run — they leave the Counted section and
  // join the main list for the day (drag one back down to remove it again).
  const [promotedCounted, setPromotedCounted] = useState(() => new Set(focusTimerState?.promotedCounted ?? load("clarity_focusPromotedCounted", [])));
  const [interBreak, setInterBreak] = useState(focusTimerState?.interBreak ?? false);   // breather between items (timer mode)
  // runner: null when idle, else { ids:[qid…], index, itemStartedAt, showTimer, counts:{qid:n} }
  const [runner, setRunner] = useState(focusTimerState?.runner ?? null);
  // plan: the on-main-page run. null when idle, else { items:[snapshot…], index, doneCurrent, lastLog }
  // Durably restored (date-scoped) from localStorage too — so an in-progress
  // clock-in session survives an app close / reload / discarded tab and the user
  // lands back on the session they left off in, not the empty To-Do view.
  const [plan, setPlan] = useState(() => {
    if (focusTimerState?.plan) return focusTimerState.plan;
    const s = load("clarity_focusPlan", null);
    if (s && s.date === todayStr()) return s.plan;
    // Nothing picked yet today: start on the last "Just a category" pick, if any.
    const cat = rememberedCategory(categories);
    return cat ? { items: [categoryPlanItem(cat)], index: 0, doneCurrent: false, lastLog: null, hidden: false } : null;
  });
  const [queueTab, setQueueTab] = useState("habits"); // panel section: "habits" | "tasks" | "categories"
  const [newCatTitle, setNewCatTitle] = useState(null);  // null = closed, string = the inline "+ New category" field
  const [planReview, setPlanReview] = useState(null); // index of an earlier completed item being reviewed, or null
  const [noteEditor, setNoteEditor] = useState(null); // { sid, draft } — the Done pill's "+ Add note" panel
  const openNoteEditor = (v) => { primeKeyboard(); setNoteEditor(v); }; // keyboard opens with the tap (keyboardNow.js)
  const [restartConfirm, setRestartConfirm] = useState(null); // reviewed item pending a restart confirmation
  const [quickResetConfirm, setQuickResetConfirm] = useState(false); // mid-run Restart tap pending confirmation
  const [editDurationConfirm, setEditDurationConfirm] = useState(false); // mid-run pencil (edit duration) tap pending confirmation
  const [modeSwitchConfirm, setModeSwitchConfirm] = useState(null); // mode id ("custom"/"stopwatch") pending confirmation to switch to
  const [showCategoryRemoveConfirm, setShowCategoryRemoveConfirm] = useState(false); // confirm gate before the drawer's Remove clears a "Just a category" pick (it resets the clock)
  const addPastRef = useRef(null); // "+ Add past session" sheet (Jade, 2026-10-11); its open state lives in SessionSheetHost
  const [removeConfirm, setRemoveConfirm] = useState(null); // { qid, kind, id, title } pending temp-vs-permanent remove choice
  const [undoDoneConfirm, setUndoDoneConfirm] = useState(null); // done-section item pending un-complete confirmation
  // True only for the render right after the check button (or an auto-completing
  // Timer) actually marks the current item done — drives the Done pill's pop/burst
  // animation. Deliberately local (not seeded from focusTimerState like plan is),
  // so switching tabs away and back remounts FocusTab with this back at false even
  // though plan.doneCurrent is still true — otherwise the animation replayed every
  // time the user just revisited the tab, not only when they actually completed it.
  const [justCompletedAnim, setJustCompletedAnim] = useState(false);
  // ...and only once: switched back off after the pop, so coming back from the
  // Chart / logged sessions page (which swaps this screen out) doesn't replay it.
  useEffect(() => {
    if (!justCompletedAnim) return;
    const t = setTimeout(() => setJustCompletedAnim(false), 900);
    return () => clearTimeout(t);
  }, [justCompletedAnim]);
  // The pop used to start in the same frame as the heavy render that marks the
  // item done (session saved, habit logged), so it stuttered (Jade, 2026-10-21).
  // The pill mounts with its animation paused (sheet-enter-wait) and is let go
  // two frames later, once that render has been painted.
  const donePillRef = useRef(null);
  useLayoutEffect(() => {
    const el = donePillRef.current;
    if (!justCompletedAnim || !el) return;
    // The nav's frosted blur (gradient backgrounds) is paused for the pop too.
    pauseNavBlur(1100);
    let r2 = 0;
    const r1 = requestAnimationFrame(() => { r2 = requestAnimationFrame(() => el.classList.remove("sheet-enter-wait")); });
    return () => { cancelAnimationFrame(r1); cancelAnimationFrame(r2); el.classList.remove("sheet-enter-wait"); };
  }, [justCompletedAnim]);
  const [viewDetailsFor, setViewDetailsFor] = useState(null); // { qid, kind, id, title } — main-card "View details" panel
  const [queueDecision, setQueueDecision] = useState(null); // "continue" modal payload: { nextLabel } or "break"
  const [queueSummary, setQueueSummary] = useState(null);   // end-of-queue: { count, mins }
  const [runnerNow, setRunnerNow] = useState(Date.now());   // ticks the optional elapsed display

  // Persist queue+runner into parent so it survives tab switches
  useEffect(() => {
    setFocusTimerState(prev => ({
      ...prev,
      queueOrder, queueSelected: [...queueSelected], queueCanceled, customTodos,
      interBreak, runner, plan, promotedCounted: [...promotedCounted],
    }));
  }, [queueOrder, queueSelected, queueCanceled, customTodos, interBreak, runner, plan, promotedCounted]); // eslint-disable-line

  // App Blocker: cover distracting apps for as long as the Clock-In session is
  // running, in either mode — starts only on a Start/Resume tap with the box
  // ticked (appBlockArmed), stops only once the session truly ends
  // (reset/exit/complete). Pausing does NOT lift the block — the only way
  // to interrupt it mid-session is revoking Usage Access in Android
  // Settings, not a button inside Clarity. Also deliberately NOT tied to
  // `plan`/tab mount: switching to another Clarity tab unmounts FocusTab (see
  // the focusTimerState sync above) while the wall-clock timer keeps running
  // underneath, and blocking should keep covering apps through that exactly
  // like the countdown does.
  //
  // Starts as null, not false, so the first run after mount ALWAYS writes:
  // native keeps its flag across process death, and assuming it was already
  // off let a stale "on" survive indefinitely — blocking apps with no session
  // running, the moment the permissions were granted.
  const appBlockingSentRef = useRef(null);
  useEffect(() => {
    if (!appBlockerIsNative) return;
    const stopwatch = mode === "stopwatch";
    // plan?.doneCurrent flips true the moment the Done/check button marks the
    // current plan item complete (handlePlanCheck only pauses running/swRunning
    // in place, it never resets them, so without this the block would otherwise
    // never lift until the whole tab/session is torn down).
    const { active, until } = appBlockingState({
      armed: appBlockArmed,
      sessionRunning: stopwatch ? swRunning : running,
      doneCurrent: !!plan?.doneCurrent,
      stopwatch,
      paused,
      startMs: startTimestampRef.current,
      durationMs: totalDurationRef.current,
    });
    const sent = `${active}:${until}`;
    if (sent === appBlockingSentRef.current) return;
    appBlockingSentRef.current = sent;
    setSessionBlocking(active, until).catch(() => {});
  }, [running, paused, swRunning, mode, appBlockArmed, plan?.doneCurrent]);

  // Durably persist everything that defines today's-plan contents/arrangement —
  // order, cancellations, custom to-dos, and promoted counted habits — so all of
  // it survives app close / reload / the tab being discarded. The in-memory
  // focusTimerState above is lost in those cases; nothing here should change on
  // its own unless the user actually re-drags/re-decides.
  useEffect(() => { save("clarity_focusQueueOrder", queueOrder); }, [queueOrder]);
  useEffect(() => { save("clarity_focusQueueCanceled", { date: todayStr(), items: queueCanceled }); }, [queueCanceled]);
  // Migration/guard: habit/task "just for today" removals used to live in this
  // list; that's now handled entirely through addToClockIn + clockInRestoreDate
  // (see confirmRemoveTemporary below), so any leftover h:/t: entries here are
  // stale and would otherwise block the item forever no matter what
  // Add-to-Clock-In says. Only customs (no checkbox of their own) still use
  // this list, so strip everything else on mount.
  useEffect(() => {
    setQueueCanceled(prev => {
      const next = prev.filter(c => !(c.qid.startsWith("h:") || c.qid.startsWith("t:")));
      return next.length === prev.length ? prev : next;
    });
  }, []); // eslint-disable-line
  useEffect(() => { save("clarity_focusCustomTodos", customTodos); }, [customTodos]);
  useEffect(() => { save("clarity_focusPromotedCounted", [...promotedCounted]); }, [promotedCounted]);
  // In-progress session: keep the running plan around across reloads (date-scoped,
  // cleared once the plan is finished/exited so a stale one never resurfaces).
  useEffect(() => {
    if (plan) save("clarity_focusPlan", { date: todayStr(), plan });
    else localStorage.removeItem("clarity_focusPlan");
  }, [plan]);

  // ── Daily rollover ───────────────────────────────────────────────────────
  // The main-page "plan" run, its item pointer, and today's skips are all
  // scoped to "today" — but on mobile the app is usually resumed rather than
  // reloaded, so nothing else re-checks the date after midnight. Without this,
  // yesterday's plan (and its pointer sitting on the last item) survives into
  // today: the run never restarts at item 1, and pressing Continue after a
  // reorder takes the "edit the existing plan in place" path instead of
  // starting fresh, so the drag order looks like it didn't save.
  // The drag-arranged queueOrder, promotedCounted habits, and customTodos are
  // deliberately left alone — only the run itself restarts each day.
  const queueDayRef = useRef(load("clarity_focusQueueDay", todayStr()));
  useEffect(() => {
    const checkRollover = () => {
      const today = todayStr();
      if (queueDayRef.current === today) return;
      // Don't cut off a segment actively being timed — try again next tick,
      // once the user stops it or reopens the app.
      const sessionActive = (plan && !plan.doneCurrent && (running || swRunning)) || runner?.active;
      if (sessionActive) return;
      queueDayRef.current = today;
      save("clarity_focusQueueDay", today);
      setPlan(null);
      setRunner(null);
      setQueueCanceled([]);
      setQueueSelected(new Set());
    };
    checkRollover();
    const id = setInterval(checkRollover, 30000);
    window.addEventListener("focus", checkRollover);
    document.addEventListener("visibilitychange", checkRollover);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", checkRollover);
      document.removeEventListener("visibilitychange", checkRollover);
    };
  }, [plan, running, swRunning, runner]); // eslint-disable-line

  // Bridges the runner's mode-aware auto-complete (assigned later, where completeCurrent is in scope)
  const runnerAutoCompleteRef = useRef(() => {});

  // "Classic" mode was removed — fall back to Custom for anyone with it saved
  useEffect(() => { if (mode === "classic") { setMode("custom"); save("clarity_focusMode", "custom"); } }, [mode]); // eslint-disable-line

  // Tick the runner clock once a second while active; also drives timer-mode auto-complete
  useEffect(() => {
    if (!runner?.active) return;
    const id = setInterval(() => { setRunnerNow(Date.now()); runnerAutoCompleteRef.current(); }, 1000);
    return () => clearInterval(id);
  }, [runner?.active, runner?.index]); // eslint-disable-line

  const qDnDSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 160, tolerance: 8 } }),
  );

  // ── SVGs
  const isActivelyRunning = (running && !paused) || (swRunning && !swPaused);

  // Single ring, Forest-style: before Start is pressed (custom mode), the very
  // same ring IS the duration picker — drag the dot to set it. The moment
  // Start is pressed it seamlessly becomes the countdown ring; no separate
  // small dials.
  // Ring, numbers and labels are black, or white on dark backgrounds, never the
  // accent (Jade, 2026-10-24); the empty part of the ring is the same colour, faint.
  const TimerDisplay = () => {
    const ci = clockInInk();
    const textMain = ci.ink;
    const textSoft = ci.soft;
    const trackCol = ci.faint;
    const fillCol  = ci.ink;

    if (mode === "custom" && !running) {
      return (
        <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
          {/* The picker draws its number / label / handle from these. */}
          <div style={{ "--c-text": ci.ink, "--c-text-soft": ci.soft, "--c-bg-card": ci.dark ? "#3a4048" : "#ffffff", display: "contents" }}>
          <DurationRingPicker
            value={Number(customWork) || 25} min={5} max={200} step={5}
            size={CI_RING} strokeWidth={CI_RING_STROKE}
            color={fillCol} trackColor={trackCol}
            onChange={(n) => { setCustomWork(n); save("clarity_focusCustomWork", n); handleReset(n * 60); }} />
          </div>
        </div>
      );
    }

    const R = CI_RING_R; const CIRC = 2 * Math.PI * R;
    const offset = CIRC * (1 - pct);
    const C = CI_RING_CANVAS, cxy = C / 2;
    return (
      <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>
        {/* Circular ring: same size and box as the picker above, so Start doesn't shrink it. */}
        <div style={{ position: "relative", width: C, height: C, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width={C} height={C} viewBox={`0 0 ${C} ${C}`} style={{ position: "absolute", top: 0, left: 0 }}>
            <circle cx={cxy} cy={cxy} r={R} fill="none" stroke={trackCol} strokeWidth={CI_RING_STROKE} />
            <circle cx={cxy} cy={cxy} r={R} fill="none" stroke={fillCol} strokeWidth={CI_RING_STROKE}
              strokeDasharray={CIRC} strokeDashoffset={offset}
              strokeLinecap="round"
              transform={`rotate(-90 ${cxy} ${cxy})`}
              style={{ transition: "stroke-dashoffset 1.05s linear" }} />
          </svg>
          {/* The time sits in the ring's exact centre; the "Timer" label hangs below it
              without pushing it up (Jade, 2026-10-24). */}
          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{ fontSize: 52, fontWeight: 800, color: (mins === "--") ? ci.faint : textMain, fontFamily: "Quicksand", letterSpacing: -1.5, lineHeight: 1 }}>
              {mins}:{secs}
            </div>
            <div style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", marginTop: 10, fontSize: 12, fontWeight: 700, color: textSoft, letterSpacing: 1, textTransform: "uppercase", whiteSpace: "nowrap" }}>Timer</div>
          </div>
        </div>
      </div>
    );
  };

  const StopwatchDisplay = () => {
    const ci = clockInInk();
    const textMain = ci.ink;
    const textSoft = ci.soft;
    // Elapsed view: no ring — just the time + label, centered in the same vertical space.
    return (
      <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>
        <div style={{ position: "relative", width: CI_RING_CANVAS, height: CI_RING_CANVAS, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6 }}>
          <div style={{ fontSize: wide ? (swH > 0 ? "clamp(64px, 7.5vw, 116px)" : "clamp(72px, 9vw, 140px)") : (swH > 0 ? 52 : 58), fontWeight: 800, color: textMain, fontFamily: wide ? "'Nunito', sans-serif" : "Quicksand", letterSpacing: wide ? -3 : -1.5, lineHeight: 1, whiteSpace: "nowrap" }}>
            {swDisplay}
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: textSoft, letterSpacing: 1, textTransform: "uppercase" }}>Stopwatch</div>
        </div>
      </div>
    );
  };



  // ── Task Category Picker modal

  // ── Habit Picker dropdown
  // Today's minutes per habit label
  // ── Category picker state ─────────────────────────────────────────────────────
  const [showNewCatForm, setShowNewCatForm] = React.useState(false);
  const [newCatForm, setNewCatForm] = React.useState({ emoji: "🎯", title: "", color: "#f4a7b9" });
  const [sessionNote, setSessionNote] = React.useState("");

  // ── Recent activities per category (derived from past clock-ins) ──────────────
  // Tapping a chip refills the note so you can re-run a past activity fast.
  const [dismissedRecents, setDismissedRecents] = React.useState(() => load("clarity_focusRecentsDismissed", {}));
  useEffect(() => { save("clarity_focusRecentsDismissed", dismissedRecents); }, [dismissedRecents]);

  const categoryRecents = React.useMemo(() => {
    const catId = assignedItem?.categoryId;
    if (!catId) return [];
    const dism = dismissedRecents[catId] || {};
    const seen = new Map(); // label -> latest timestamp used
    for (const s of focusSessions) {
      if (s.categoryId !== catId) continue;
      const lbl = (s.label || "").trim();
      if (!lbl) continue;
      const ts = s.completedAt || s.startedAt || "";
      if (!seen.has(lbl) || ts > seen.get(lbl)) seen.set(lbl, ts);
    }
    return [...seen.entries()]
      // Hidden only if dismissed at/after its most recent use (reappears if used again)
      .filter(([lbl, ts]) => !(dism[lbl] && dism[lbl] >= ts))
      .sort((a, b) => (b[1] || "").localeCompare(a[1] || ""))
      .map(([lbl]) => lbl)
      .slice(0, 6);
  }, [assignedItem, focusSessions, dismissedRecents]);

  const dismissRecent = (lbl) => {
    const catId = assignedItem?.categoryId;
    if (!catId) return;
    setDismissedRecents(prev => ({ ...prev, [catId]: { ...(prev[catId] || {}), [lbl]: new Date().toISOString() } }));
    if (sessionNote === lbl) setSessionNote("");
  };

  const CAT_COLORS = ["#f4a7b9","#b5d5f5","#b5e6c8","#f5d6a7","#d4b5f5","#f5b5d6","#a7d4f5","#b5f5d4","#f5c5a7","#c5f5a7"];
  const EMOJI_OPTS = ["🎯","🌿","📚","💪","🎨","🏃","🧘","💡","🎵","✍️","🍳","🌱","💼","🎮","📝","🔬","🎭","🏋️","☕","🌙"];

  const handleSaveNewCat = () => {
    if (!newCatForm.title.trim()) return;
    const newCat = { id: `cat_${Date.now()}`, emoji: newCatForm.emoji, title: newCatForm.title.trim(), color: newCatForm.color, isGoal: false, goalDescription: "", createdAt: new Date().toISOString() };
    // categories is passed as prop — need to bubble up via a setter
    // We'll dispatch a custom event that App.jsx can listen to
    window.dispatchEvent(new CustomEvent("clarity:addCategory", { detail: newCat }));
    setNewCatForm({ emoji: "🎯", title: "", color: "#f4a7b9" });
    setShowNewCatForm(false);
    // Auto-assign the new category
    setAssignedItem({ key: `cat-${newCat.id}`, label: newCat.title, emoji: newCat.emoji, categoryLabel: null, color: newCat.color, categoryId: newCat.id });
    setHeaderLabel(`${newCat.emoji} ${newCat.title}`);
    setShowHabitPicker(false);
  };

  const CategoryPicker = ({ standalone = false } = {}) => (
    <div style={standalone ? {} : { marginTop: 12, borderTop: "1px solid #f0d0e8", paddingTop: 12 }}>

      {/* Category list */}
      {categories.length === 0 && !showNewCatForm && (
        <div style={{ textAlign: "center", padding: "16px 0 8px" }}>
          <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: "var(--c-text)" }}>No categories yet</p>
          <p style={{ margin: "0 0 14px", fontSize: 12, color: "var(--c-text-soft)" }}>Create one to start tracking your time</p>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12,
        ...(standalone ? {} : { maxHeight: 264, overflowY: "auto", paddingRight: 2 }) }}>
        {(() => {
          const isSelected = assignedItem?.key === "cat-uncategorized";
          const catColor = "#b0a4bc";
          return (
            <button
              onClick={() => {
                setAssignedItem({ key: "cat-uncategorized", label: "Uncategorized", emoji: "🗂", categoryLabel: null, color: catColor, categoryId: null });
                setHeaderLabel("🗂 Uncategorized");
                setShowNudge(false);
                setShowHabitPicker(false);
              }}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "10px 14px", borderRadius: 14, width: "100%",
                border: isSelected ? `2px solid ${catColor}` : `1.5px solid ${catColor}44`,
                background: isSelected ? `${catColor}22` : `${catColor}0d`,
                cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                transition: "all 0.15s ease",
              }}>
              <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }}>🗂</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: isSelected ? "#3a3040" : "#4a3050", flex: 1 }}>
                Uncategorized
              </span>
              {isSelected && <span style={{ fontSize: 13, color: catColor, fontWeight: 700 }}>✓</span>}
            </button>
          );
        })()}
      </div>

      {categories.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12,
          ...(standalone ? {} : { maxHeight: 264, overflowY: "auto", paddingRight: 2 }) }}>
          {categories.map(cat => {
            const isSelected = assignedItem?.categoryId === cat.id;
            const catColor   = cat.color || "#f4a7b9";
            return (
              <button key={cat.id}
                onClick={() => {
                  setAssignedItem({ key: `cat-${cat.id}`, label: cat.title, emoji: cat.emoji || "🎯", categoryLabel: null, color: catColor, categoryId: cat.id });
                  setHeaderLabel(`${cat.emoji || "🎯"} ${cat.title}`);
                  setShowNudge(false);
                  setShowHabitPicker(false);
                }}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "10px 14px", borderRadius: 14, width: "100%",
                  border: isSelected ? `2px solid ${catColor}` : `1.5px solid ${catColor}44`,
                  background: isSelected ? `${catColor}22` : `${catColor}0d`,
                  cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                  transition: "all 0.15s ease",
                }}>
                <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }}>{cat.emoji || "🎯"}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: isSelected ? "#3a3040" : "#4a3050", flex: 1 }}>
                  {cat.title}
                </span>
                {isSelected && <span style={{ fontSize: 13, color: catColor, fontWeight: 700 }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* Inline new category form */}
      {showNewCatForm ? (
        <div style={{ background: "var(--c-bg-card)", border: "1.5px solid #f0d0e8", borderRadius: 14, padding: "14px 14px 12px", marginBottom: 10 }}>
          <p style={{ ...S.label, marginBottom: 10 }}>NEW CATEGORY</p>

          {/* Emoji picker */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 }}>
            {EMOJI_OPTS.map(e => (
              <button key={e} onClick={() => setNewCatForm(f => ({ ...f, emoji: e }))}
                style={{ fontSize: 18, padding: "4px 6px", borderRadius: 8, border: newCatForm.emoji === e ? "2px solid #d4789a" : "1.5px solid #f0d0e8", background: newCatForm.emoji === e ? "var(--c-bg-card)" : "transparent", cursor: "pointer" }}>
                {e}
              </button>
            ))}
          </div>

          {/* Name input */}
          <input
            value={newCatForm.title}
            onChange={e => setNewCatForm(f => ({ ...f, title: e.target.value }))}
            onKeyDown={e => e.key === "Enter" && handleSaveNewCat()}
            placeholder="Category name…"
            autoFocus
            style={{ ...S.input, width: "100%", boxSizing: "border-box", marginBottom: 10, fontSize: 13 }} />

          {/* Color swatches */}
          <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
            {CAT_COLORS.map(c => (
              <button key={c} onClick={() => setNewCatForm(f => ({ ...f, color: c }))}
                style={{ width: 24, height: 24, borderRadius: "50%", background: c, border: newCatForm.color === c ? "3px solid #3a3040" : "2px solid transparent", cursor: "pointer", padding: 0 }} />
            ))}
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleSaveNewCat} className="clarity-add-btn"
              style={{ ...S.addBtn, flex: 1, fontSize: 13, padding: "9px 0" }}>
              <FolderPlus size={14} style={{marginRight:4}} />Create & assign
            </button>
            <button onClick={() => setShowNewCatForm(false)}
              style={{ ...S.btn, padding: "9px 16px", fontSize: 13 }}>
              <X size={14} style={{marginRight:4}} />Cancel
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setShowNewCatForm(true)}
          style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px 14px", borderRadius: 12, border: "1.5px dashed #e0a8cc", background: "transparent", color: "var(--c-text-soft)", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600, transition: "all 0.15s" }}
          onMouseEnter={e => { e.currentTarget.style.background = "#ffffff"; e.currentTarget.style.color = "#d4789a"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#c090b0"; }}>
          ＋ New category
        </button>
      )}

      {assignedItem && (
        <button onClick={clearAssignment}
          style={{ marginTop: 8, background: "transparent", border: "none", color: "#c09090", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
          ✕ Clear
        </button>
      )}
    </div>
  );

  // ── Assigned badge ────────────────────────────────────────────────────────────
  const AssignedBadge = () => assignedItem ? (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      background: assignedItem.color ? `${assignedItem.color}18` : "linear-gradient(135deg, #ffffff 0%, #f8d8f0 100%)",
      border: `1.5px solid ${assignedItem.color ? assignedItem.color + "55" : "#f0c8e0"}`,
      borderRadius: 14, padding: "12px 16px",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 26, lineHeight: 1, background: "var(--c-bg-card)", borderRadius: 10, padding: "4px 8px", border: `1px solid ${assignedItem.color ? assignedItem.color + "44" : "#f0d0e8"}` }}>
          {assignedItem.emoji}
        </span>
        <div>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--c-text)" }}>
            {assignedItem.label}
          </p>
          {sessionNote && (
            <p style={{ margin: "2px 0 0", fontSize: 11, color: "var(--c-text-soft)", fontWeight: 500 }}>
              {sessionNote}
            </p>
          )}
        </div>
      </div>
      <button onClick={() => { if (!showHabitPicker) setSessionNote(""); setShowHabitPicker(v => !v); }}
        style={{ background: showHabitPicker ? "#fff" : "rgba(255,255,255,0.6)", border: `1.5px solid ${assignedItem.color ? assignedItem.color + "66" : "#f0c8e0"}`, borderRadius: 10, padding: "6px 12px", color: "var(--c-text)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}>
        {showHabitPicker ? "✕ Close" : "✏️ Change"}
      </button>
    </div>
  ) : null;

  // ── Assign button ─────────────────────────────────────────────────────────────
  const AssignButton = () => !assignedItem ? (
    <button onClick={() => setShowHabitPicker(v => !v)}
      style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "14px 16px", background: showHabitPicker ? "var(--c-bg-card)" : "var(--c-bg-card)", border: `1.5px ${showHabitPicker ? "solid var(--c-primary)" : "dashed var(--c-border)"}`, borderRadius: 14, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s ease" }}>
      <span style={{ fontSize: 20 }}>🏷️</span>
      <div style={{ textAlign: "left" }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: showHabitPicker ? "var(--c-text-accent)" : "var(--c-text-mid)" }}>Assign a category</p>
        <p style={{ margin: "1px 0 0", fontSize: 11, color: "var(--c-text-soft)" }}>Sessions log better with a category</p>
      </div>
      <span style={{ marginLeft: "auto", fontSize: 16, color: showHabitPicker ? "var(--c-text-accent)" : "var(--c-text-soft)", transform: showHabitPicker ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▾</span>
    </button>
  ) : null;

  // ── Deep Mode overlay

  // ── Clock-in analytics sub-page (donut chart + logged sessions) ──
  // Built here but returned at the very end of the component (after every hook
  // below has run) instead of via an early return, so hooks further down don't
  // get skipped on renders where this sub-page is showing — an early return at
  // this point would call those hooks conditionally, which React disallows.
  const statsPage = !showStats ? null : (() => {
    const ci = clockInInk();
    // Week / Month / Year dropdown beside the menu button (the donut page portals it
    // into the first slot), then ⋯ (same, second slot) and a stopwatch icon
    // back to the timer at the far right (Jade, 2026-10-24). In the app's top
    // bar beside the menu button on a phone, else a row above the chart.
    const plainIcon = { width: 40, height: 40, minHeight: 0, minWidth: 0, padding: 0, border: "none", background: "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: ci.ink, flexShrink: 0 };
    // Desktop: the Week / Month / Year dropdown and the dates sit centred above the
    // totals instead (FocusPieChart portals them into that slot), and only ⋯ and the
    // stopwatch stay at the top right (Jade, 2026-11-02).
    const statsTop = (
      <>
        {!wide && <div ref={setStatsViewSlot} style={{ display: "flex", alignItems: "center", marginRight: "auto" }} />}
        <div ref={setStatsToolbarSlot} style={{ display: "flex", alignItems: "center" }} />
        <button onClick={() => setShowStats(false)} title="Back to timer" aria-label="Back to timer" style={plainIcon}>
          <Timer size={23} strokeWidth={2.3} />
        </button>
      </>
    );
    return (
    <div className="ci-desk-center" style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: wide ? 920 : 420, width: "100%", margin: "0 auto" }}>
      {topBarSlotNode ? createPortal(statsTop, topBarSlotNode)
        : <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>{statsTop}</div>}
      {wide && <div ref={setStatsViewSlot} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14 }} />}
      <ProfileTab section="focus"
        focusSessions={focusSessions} setFocusSessions={setFocusSessions}
        habits={habits} setHabits={setHabits}
        habitLogs={habitLogs} setHabitLogs={setHabitLogs}
        habitSkips={habitSkips} setHabitSkips={setHabitSkips}
        tasks={tasks} setTasks={setTasks} categories={categories} setCategories={setCategories}
        statsToolbarSlotNode={statsToolbarSlot} statsViewSlotNode={statsViewSlot} weekStart={weekStart} bare wide={wide}
        inkKey={ci.ink + ci.glass.background /* ProfileTab is memoised: re-render it when the background changes */} />
    </div>
    );
  })();

  // ══ Queue: build today's items, apply order, derive lists ════════════════════
  const qToday = todayStr();
  const catTitleFor = (goalId) => categories.find(c => c.id === goalId)?.title || null;
  const habitDoneCount = (h) => {
    if (h.habitType === "nottodo" || h.habitType === "limited")
      return isHabitDoneOn(h, habitLogs, qToday, habitSkips) ? (h.dailyCount || 1) : 0;
    const cur = habitLogs[qToday]?.[h.id];
    return typeof cur === "number" ? cur : (cur ? (h.dailyCount || 1) : 0);
  };
  // Restricted habits (avoid/limit/time-restricted) are things the user doesn't
  // want to build — they never belong in a "to do" run, due or not.
  const isRestrictedType = (h) => h.habitType === "nottodo" || h.habitType === "limited";
  // Counted habits (X times a week/month, no specific due date) get their own
  // optional section instead of the main due list.
  const isCountedFreq = (h) => h.frequencyType === "count" || (h.frequencyType === "monthly" && h.monthlyMode === "count");
  const habitStarted = (h) => qToday >= (h.habitStartDate || localDateStr(h.createdAt) || "");
  // Opt-out flag: undefined/true = included (keeps every existing habit/task
  // showing up exactly as before), only an explicit false hides it.
  const wantsClockIn = (it) => it.addToClockIn !== false;
  // Base pool of things you could run today
  const queueBase = [
    ...habits
      .filter(h => !isRestrictedType(h) && isHabitDueOn(h, qToday) && habitStarted(h) && wantsClockIn(h))
      .map(h => ({ qid: `h:${h.id}`, kind: "habit", ref: h, title: h.name, color: h.color || "#d4789a", goalId: h.goalId })),
    ...habits
      .filter(h => !isRestrictedType(h) && isCountedFreq(h) && habitStarted(h) && wantsClockIn(h))
      // countedByNature stays true so it can round-trip; `counted` (whether it sits in
      // the Counted section) flips off once the card has been dragged up into the run.
      .map(h => ({ qid: `h:${h.id}`, kind: "habit", ref: h, title: h.name, color: h.color || "#d4789a", goalId: h.goalId, countedByNature: true, counted: !promotedCounted.has(`h:${h.id}`) })),
    ...tasks
      .filter(t => isTaskOccurringOn(t, qToday) && !isTaskDoneOn(t, qToday, taskOccurrenceOverrides) && wantsClockIn(t))
      .map(t => ({ qid: `t:${t.id}`, kind: "task", ref: t, title: t.title, color: t.color || "#7aa5d4", goalId: t.goalId })),
    // Custom to-dos reuse the same due/counted rules as habits (isHabitDueOn reads
    // frequencyType generically): a plain or "daily" to-do is due every day; a
    // recurring one only shows on its days/dates; a "count" (X/week) or
    // monthly-count one is counted-by-nature and lives in the Flexible section
    // until dragged up, same as a counted habit.
    ...customTodos
      .filter(c => !isCountedFreq(c) && isHabitDueOn(c, qToday))
      .map(c => ({ qid: `c:${c.id}`, kind: "custom", ref: c, title: c.title, color: "#caa0d4", goalId: c.goalId || null, emoji: c.emoji || null })),
    ...customTodos
      .filter(c => isCountedFreq(c))
      .map(c => ({ qid: `c:${c.id}`, kind: "custom", ref: c, title: c.title, color: "#caa0d4", goalId: c.goalId || null, emoji: c.emoji || null, countedByNature: true, counted: !promotedCounted.has(`c:${c.id}`) })),
  ];
  // Habits and tasks are shown in their own sections in the Assign-to-do panel,
  // so within any subset we always render/reorder habits before tasks/to-dos
  // (relative order inside each group is preserved).
  const groupHabitsFirst = (arr) => [...arr.filter(it => it.kind === "habit"), ...arr.filter(it => it.kind !== "habit")];
  const queueBaseMap = new Map(queueBase.map(it => [it.qid, it]));
  const canceledSet = new Set(queueCanceled.map(c => c.qid));
  // Apply saved order (keep new items appended, drop stale ids)
  const orderedQids = (queueOrder && queueOrder.length)
    ? [...queueOrder.filter(id => queueBaseMap.has(id)), ...queueBase.map(i => i.qid).filter(id => !queueOrder.includes(id))]
    : queueBase.map(i => i.qid);
  // De-duplicate: a stale/corrupted queueOrder can list the same qid twice
  // (e.g. a leftover id after an item was re-created). Only that exact-qid
  // case is collapsed — two distinct records that merely share a title (a
  // one-time "Workout" task and a separately-created recurring "Workout"
  // task, say) are two different real items and must both stay, so there is
  // no title-based collision guard here.
  const queueItems = (() => {
    const out = [];
    const seenQid = new Set();
    for (const id of orderedQids) {
      const it = queueBaseMap.get(id);
      if (!it || canceledSet.has(it.qid) || seenQid.has(it.qid)) continue;
      seenQid.add(it.qid);
      out.push(it);
    }
    return out;
  })();
  // Single-pick only — the guided, multi-item "run through the queue" flow is gone,
  // so tapping a card selects just that one (deselecting whatever was picked before).
  const toggleQueueSelect = (qid) => setQueueSelected(prev => (prev.has(qid) ? new Set() : new Set([qid])));
  const cancelQueueItem = (qid, reason) => {
    setQueueCanceled(prev => [...prev.filter(c => c.qid !== qid), { qid, reason }]);
    setExpandedQueueId(null);
    setQueueSelected(prev => { const n = new Set(prev); n.delete(qid); return n; });
  };
  const handleQueueDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const activeIt = queueBaseMap.get(active.id);
    if (!activeIt) return;
    // Dropped onto the empty Counted drop zone → send a counted habit back to the section.
    if (over.id === "counted-zone") {
      if (activeIt.countedByNature) setPromotedCounted(prev => { const n = new Set(prev); n.delete(active.id); return n; });
      return;
    }
    const overIt = queueBaseMap.get(over.id);
    if (!overIt) return;
    // A counted habit dragged over a main-list card joins today's run; dragged back
    // over a Counted-section card, it returns to the section. (`counted` is the flag
    // for "currently sits in the Counted section".)
    if (activeIt.countedByNature) {
      setPromotedCounted(prev => {
        const n = new Set(prev);
        overIt.counted ? n.delete(active.id) : n.add(active.id);
        return n;
      });
    }
    // Reorder within the combined visible list (main cards first, then Counted cards)
    const doneQids = new Set(doneQueueItems.map(it => it.qid));
    const mainV = groupHabitsFirst(queueItems.filter(it => !it.counted && !doneQids.has(it.qid))).map(i => i.qid);
    const countedV = queueItems.filter(it => it.counted && !doneQids.has(it.qid)).map(i => i.qid);
    const visible = [...mainV, ...countedV];
    const from = visible.indexOf(active.id), to = visible.indexOf(over.id);
    if (from < 0 || to < 0) return;
    const reordered = arrayMove(visible, from, to);
    // Persist full order: reordered visible items + canceled ids kept as-is
    setQueueOrder([...reordered, ...queueCanceled.map(c => c.qid)]);
  };
  // Custom to-do create/edit — one draft shape for both flows (see customTodoEditor).
  const blankCustomTodoDraft = () => ({
    id: null, title: "", description: "", goalId: null, emoji: null,
    frequencyType: "daily", days: [], weeklyCount: 3,
    monthlyMode: "dates", monthDates: [], monthlyCount: 10,
  });
  const openNewCustomTodo = () => { setShowDeleteTodoConfirm(false); setCustomTodoEditor(blankCustomTodoDraft()); };
  const openEditCustomTodo = (c) => {
    setExpandedQueueId(null);
    setShowDeleteTodoConfirm(false);
    setCustomTodoEditor({
      id: c.id, title: c.title, description: c.description || "", goalId: c.goalId || null,
      emoji: c.emoji || null, frequencyType: c.frequencyType || "daily",
      days: c.days || [], weeklyCount: c.weeklyCount || 3,
      monthlyMode: c.monthlyMode || "dates", monthDates: c.monthDates || [], monthlyCount: c.monthlyCount || 10,
    });
  };
  // Save the draft — linking a category (goalId) routes its completed session under
  // that category in the donut/logs; no category → uncategorized. A "count" (weekly)
  // or monthly-count frequency makes it a Flexible/counted to-do, same as habits.
  const saveCustomTodoEditor = (goalId = customTodoEditor?.goalId ?? null) => {
    const d = customTodoEditor;
    const title = d?.title.trim();
    if (!title) return;
    const base = {
      title, description: d.description.trim(), goalId: goalId || null, emoji: d.emoji || null,
      frequencyType: d.frequencyType, days: d.days, dayLabels: "named",
      weeklyCount: d.weeklyCount, monthlyMode: d.monthlyMode, monthDates: d.monthDates, monthlyCount: d.monthlyCount,
    };
    setCustomTodos(prev => d.id
      ? prev.map(c => c.id === d.id ? { ...c, ...base } : c)
      : [...prev, { id: `${Date.now()}`, ...base }]);
    setCustomTodoEditor(null);
  };
  const deleteCustomTodoFromEditor = () => {
    if (customTodoEditor?.id) setCustomTodos(prev => prev.filter(c => c.id !== customTodoEditor.id));
    setShowDeleteTodoConfirm(false);
    setCustomTodoEditor(null);
  };
  // Edit a queued item using the real editors (closes the panel, routes by kind)
  const editQueueItem = (it) => {
    setExpandedQueueId(null);
    setShowQueuePanel(false);
    if (it.kind === "task") onEditTask(it.ref);
    else if (it.kind === "habit") onEditHabit(it.ref);
  };
  // ── Logging + completion ────────────────────────────────────────────────────
  const logQueueSession = (label, mins, goalId) => {
    const safeMins = Math.max(0, mins);
    setFocusSessions(p => [...p, {
      date: fmt(new Date(Date.now() - safeMins * 60000)), minutes: safeMins, completedAt: new Date().toISOString(),
      startedAt: new Date(Date.now() - safeMins * 60000).toISOString(),
      label, categoryLabel: catTitleFor(goalId), categoryId: goalId || null,
    }]);
    window.dispatchEvent(new CustomEvent("clarity:sessionComplete", { detail: { categoryLabel: catTitleFor(goalId), label } }));
  };
  const completeTaskNow = (task) => toggleTaskDoneOn(task, qToday, setTasks, setTaskOccurrenceOverrides, true);
  // Increment a habit by one toward its daily target; returns whether it's now fully done.
  // Unit habits (habitUnits.js) aren't counted in reps: a Time habit gets the
  // session's minutes (`mins`), and any other unit (steps, pages, …) opens the
  // Add amount sheet so the amount can be entered — the session itself is
  // logged either way, and the item counts as finished for the run.
  const bumpHabitOnce = (habit, mins = 0) => {
    if (isAmountHabit(habit)) {
      const target = habit.dailyCount || 1;
      if (unitOf(habit) !== "time") { setFocusAmountAtStart(false); setFocusAmountSheetId(habit.id); return true; }
      const next = addToLog(habit, habitLogs[qToday]?.[habit.id], Math.max(1, Math.round(mins || 0)));
      setHabitLogs(prev => ({ ...prev, [qToday]: { ...(prev[qToday] || {}), [habit.id]: next } }));
      return next >= target;
    }
    const isRestricted = habit.habitType === "nottodo" || habit.habitType === "limited";
    if (isRestricted) {
      setHabits(prev => prev.map(h => h.id === habit.id ? { ...h, relapseLog: (h.relapseLog || []).filter(r => r.date !== qToday) } : h));
      setHabitSkips(prev => prev.filter(s => !(s.habitId === habit.id && s.skippedDate === qToday)));
      return true;
    }
    const target = habit.dailyCount || 1;
    const cur = habitLogs[qToday]?.[habit.id];
    const curNum = typeof cur === "number" ? cur : (cur ? target : 0);
    const next = Math.min(target, curNum + 1);
    setHabitLogs(prev => ({ ...prev, [qToday]: { ...(prev[qToday] || {}), [habit.id]: next } }));
    return next >= target;
  };

  // ── "Continue": snapshot the ONE selected item into a main-page plan (single
  //    card — the guided, multi-item queue is gone). Does NOT start the timer.
  const startQueue = () => {
    const pickQid = [...queueSelected][0];
    // A category picked on the Categories tab (qid "cat:<id>").
    if (pickQid && pickQid.startsWith("cat:")) {
      const cat = categories.find(c => `cat:${c.id}` === pickQid);
      if (cat) pickCategory(cat);
      return;
    }
    const it = pickQid ? queueItems.find(i => i.qid === pickQid) : null;
    if (!it) return;
    setShowQueuePanel(false);
    setQueueSummary(null);
    const newItem = { qid: it.qid, kind: it.kind, id: it.ref.id, title: it.title, color: it.color, goalId: it.goalId, dailyCount: it.kind === "habit" ? (it.ref.dailyCount || 1) : 1 };
    // Re-picking the same item that's already the active plan just closes the
    // panel and leaves the timer exactly where it was (paused, not reset).
    if (plan && plan.items[0]?.qid === newItem.qid) return;
    setPlan({ items: [newItem], index: 0, doneCurrent: false, lastLog: null, hidden: false });
    handleReset(); handleSwReset();
  };
  const runnerItems = runner ? runner.ids.map(id => queueBaseMap.get(id)).filter(Boolean) : [];
  const runnerCurrent = runner ? runnerItems[runner.index] : null;
  const runnerNext = runner ? runnerItems[runner.index + 1] : null;

  const advanceRunner = (extraMins) => {
    const totalMins = (runner.loggedMins || 0) + (extraMins || 0);
    if (runner.index + 1 >= runner.ids.length) {
      setRunner(null);
      setQueueDecision(null);
      setQueueSummary({ count: runner.ids.length, mins: totalMins });
      return;
    }
    // Move to next item; offer a breather (timer mode + interBreak) else a continue checkpoint
    setRunner(r => ({ ...r, index: r.index + 1, itemStartedAt: Date.now(), loggedMins: totalMins }));
    setQueueDecision({ nextLabel: runnerItems[runner.index + 1]?.title || "next", mode: (mode !== "stopwatch" && interBreak) ? "break" : "continue", breakLeft: 0 });
  };

  // Complete the current runner item, logging `mins` of focus time for this segment
  const completeCurrent = (mins) => {
    const it = runnerCurrent;
    if (!it) return;
    if (it.kind === "task") {
      completeTaskNow(it.ref);
      logQueueSession(`Task: ${it.title}`, mins, it.goalId);
      sonnerToast("Task done!");
      advanceRunner(mins);
    } else if (it.kind === "habit") {
      const target = it.ref.dailyCount || 1;
      const beforeCount = Math.max(runner.counts[it.qid] || 0, habitDoneCount(it.ref));
      const newCount = Math.min(target, beforeCount + 1);
      const fullyDone = bumpHabitOnce(it.ref, mins);
      logQueueSession(`Habit: ${it.title}`, mins, it.goalId);
      if (isAmountHabit(it.ref) && !fullyDone) {
        // Time habit still short of its goal: stay on it, restart the segment timer.
        sonnerToast(`${formatProgress(it.ref, addToLog(it.ref, habitLogs[qToday]?.[it.ref.id], Math.max(1, Math.round(mins || 0))))}. Keep going!`);
        setRunner(r => ({ ...r, itemStartedAt: Date.now() }));
      } else if (fullyDone) {
        sonnerToast(`${it.title} done!`);
        advanceRunner(mins);
      } else {
        // Multi-count habit: log this rep, stay on it, restart the segment timer
        sonnerToast(`${newCount}/${target}. Keep going!`);
        setRunner(r => ({ ...r, counts: { ...r.counts, [it.qid]: newCount }, itemStartedAt: Date.now() }));
      }
    } else {
      logQueueSession(it.title, mins, null);
      sonnerToast("Done!");
      advanceRunner(mins);
    }
  };
  const handleRunnerCheck = () => {
    if (!runnerCurrent || !runner.active) return;
    completeCurrent(Math.round((Date.now() - runner.itemStartedAt) / 60000));
  };
  // Timer-mode auto-complete is wired via runnerAutoCompleteRef in the hooks section
  runnerAutoCompleteRef.current = () => {
    if (!runner?.active || mode === "stopwatch") return;
    if ((Date.now() - runner.itemStartedAt) / 1000 >= workSecs) completeCurrent(workMins);
  };
  const resumeFromDecision = () => setQueueDecision(null);
  const pauseRunner = () => { setQueueDecision(null); setRunner(r => r ? { ...r, active: false } : r); };
  const resumeRunner = () => setRunner(r => r ? { ...r, active: true, itemStartedAt: Date.now() } : r);
  const exitRunner = () => { setRunner(null); setQueueDecision(null); };

  // ── Main-page plan run ──────────────────────────────────────────────────────
  const planItems   = plan ? plan.items : [];
  const planCurrent = plan ? planItems[plan.index] : null;
  // A "Just a category" item's "Add note" belongs to the session its check button
  // just saved. Once the next one starts (or the app is reopened) that note pill is
  // stale, so drop it — it only ever shows right after a check, like Done does for
  // habits and tasks (Jade, 2026-10-11).
  const catSessionActive = !!(planCurrent && isCategoryItem(planCurrent) && (running || swRunning));
  const catHasLastLog = !!(planCurrent && isCategoryItem(planCurrent) && plan.lastLog);
  useEffect(() => {
    if (catSessionActive && catHasLastLog) setPlan(p => p && p.lastLog ? { ...p, lastLog: null } : p);
  }, [catSessionActive, catHasLastLog]);
  const catMountClearedRef = useRef(false);
  useEffect(() => {
    if (catMountClearedRef.current) return;
    catMountClearedRef.current = true;
    if (catHasLastLog) setPlan(p => p && p.lastLog ? { ...p, lastLog: null } : p);
  }, [catHasLastLog]);
  const planNext    = plan ? planItems[plan.index + 1] : null;
  const planKindLabel = (it) => it?.kind === "habit" ? "Habit" : it?.kind === "task" ? "Task" : "To-do";
  // Whether "Edit timer duration" shows in the ⋯ menu.
  const canEditTimerDuration = mode === "custom" && (running || paused);

  // Notification-shade Clock-In timer (Android). Mirrors the in-app display —
  // the system renders the live tick itself (Notification#setUsesChronometer),
  // so this only needs to re-fire on start/pause/resume/relabel, not every
  // second. Whenever the session isn't running (in either mode), this also
  // acts as the hide() call: reset/finish/session-complete all already flip
  // running/swRunning false, so no separate teardown call is needed there.
  useEffect(() => {
    // handlePlanCheck (the check/"done" button) pauses the timer IN PLACE
    // rather than stopping it — running/swRunning stay true so "Previous"
    // can still resume it — so plan.doneCurrent has to be checked on its own
    // here, or the shade notification would linger showing "paused" forever
    // instead of disappearing the moment the item is marked done.
    const doneCurrent = !!(plan && planCurrent && plan.doneCurrent);
    const active = (mode === "stopwatch" ? swRunning : running) && !doneCurrent;
    if (!active) {
      hideTimerNotification(); cancelTimerAlarm();
      // Also clears the FCM-alarm-push server mirror (see clockInSession.js)
      // — covers stop/pause/finish/session-complete/plan-item-done in one
      // place, since all of them already flip running/swRunning false here.
      clearClockInSession(auth.currentUser?.uid);
      return;
    }
    // Prefer the actual running plan item (a specific habit/task/to-do, e.g.
    // "Answer emails") over the generic category/mode label — same source
    // the in-app header uses (planCurrent.title, see the main timer card).
    // Falls back to the plain category-only clock-in (no specific item
    // picked) for label, with no separate category badge since there's
    // nothing more specific to distinguish it from.
    const activeItem = (plan && planCurrent && !plan.doneCurrent) ? planCurrent : null;
    // Deliberately NOT timerHeader here: its "Work"/"Stopwatch" fallback is
    // in-app wording for the label above the ring, and reads as a stray
    // category name in the notification shade. With nothing attached, the
    // notification just names what's running — "Clock-In", under the
    // "Clarity" app-name line the system draws above it.
    const label = activeItem ? activeItem.title : (headerLabel || "Clock-In");
    const subText = activeItem && !isCategoryItem(activeItem) ? (catTitleFor(activeItem.goalId) || null) : null;
    if (mode === "stopwatch") {
      // No end time to ring for — a stopwatch counts up indefinitely.
      cancelTimerAlarm();
      clearClockInSession(auth.currentUser?.uid);
      if (swPaused) {
        showTimerNotification({ mode: "stopwatch", label, subText, whenMs: Date.now(), paused: true, frozenMs: swAccRef.current });
      } else {
        const startMs = (swStartRef.current ?? Date.now()) - swAccRef.current;
        showTimerNotification({ mode: "stopwatch", label, subText, whenMs: startMs, paused: false });
      }
    } else {
      if (paused) {
        // Paused: no fixed end time to ring for until resumed — the effect
        // re-runs (paused is a dep) and reschedules with the new end time.
        cancelTimerAlarm();
        clearClockInSession(auth.currentUser?.uid);
        showTimerNotification({ mode: "countdown", label, subText, whenMs: Date.now(), paused: true, frozenMs: pausedRemainingRef.current ?? 0 });
      } else {
        const dur = totalDurationRef.current ?? workSecs * 1000;
        const start = startTimestampRef.current ?? Date.now();
        const whenMs = start + dur;
        showTimerNotification({ mode: "countdown", label, subText, whenMs, paused: false });
        scheduleTimerAlarm({ triggerAt: whenMs, title: label, body: "Clock-In session complete" });
        syncClockInSession(auth.currentUser?.uid, { endsAt: whenMs, label });
      }
    }
  }, [running, paused, swRunning, swPaused, mode, headerLabel, assignedItem?.label, plan?.doneCurrent, planCurrent?.qid, planCurrent?.title, planCurrent?.goalId]); // eslint-disable-line

  // Keep the screen from auto-locking while a timer/stopwatch is actively
  // ticking (not paused, not checked off) — see keepAwake.js. Same "active"
  // rule as the shade notification above, minus the mode-specific stuff.
  useEffect(() => {
    const doneCurrent = !!(plan && planCurrent && plan.doneCurrent);
    const active = isActivelyRunning && !doneCurrent;
    if (active) enableKeepAwake(); else disableKeepAwake();
    return () => { disableKeepAwake(); };
  }, [isActivelyRunning, plan?.doneCurrent]); // eslint-disable-line

  // Picks up a Pause/Resume tap made directly on the notification (works
  // without the app open — see TimerNotificationActionReceiver) and applies
  // the same toggle to the in-app timer/stopwatch state, so the running
  // session and the on-screen display don't drift from what the
  // notification showed the whole time it was closed. Checked on mount and
  // whenever the app becomes visible again — pulling the shade down over an
  // open app also fires visibilitychange, so a tap made without leaving the
  // app gets picked up too, not just the "app was fully closed" case.
  useEffect(() => {
    const reconcile = async () => {
      if (document.visibilityState !== "visible") return;
      const toggle = await consumePendingTimerToggle();
      if (!toggle) return;
      // Apply the values the notification captured at the moment its button
      // was actually tapped — frozenMs (remaining/elapsed at pause) and
      // whenMs (the running basis at resume) — NOT handleStartPause()/
      // handleSwStartPause(), which re-derive both from Date.now(). This
      // runs when the app is next brought to the foreground, which can be
      // minutes after the tap: re-deriving counts that whole gap as time
      // that kept running, so a timer paused from the shade would come back
      // showing less remaining than the notification froze it at (and a
      // resumed one would come back having run too long).
      if (toggle.mode === "stopwatch" && mode === "stopwatch") {
        if (toggle.paused && swRunning && !swPaused) {
          restoreStopwatchPaused(toggle.frozenMs);
        } else if (!toggle.paused && swRunning && swPaused) {
          // Stopwatch: whenMs is the virtual start time, i.e. elapsed = now - whenMs.
          swAccRef.current = 0;
          swStartRef.current = toggle.whenMs;
          setSwElapsed(Math.max(0, Date.now() - toggle.whenMs));
          setSwPaused(false);
          saveSwState();
        }
      } else if (toggle.mode === "countdown" && mode !== "stopwatch") {
        const dur = totalDurationRef.current ?? load("clarity_focusDuration", null) ?? workSecs * 1000;
        if (toggle.paused && running && !paused) {
          restoreTimerPaused(toggle.frozenMs, dur);
        } else if (!toggle.paused && running && paused) {
          // Countdown: whenMs is the epoch ms it now hits zero.
          startTimestampRef.current = toggle.whenMs - dur;
          totalDurationRef.current = dur;
          pausedRemainingRef.current = null;
          save("clarity_focusStart", startTimestampRef.current);
          save("clarity_focusDuration", dur);
          save("clarity_focusPaused", "0");
          localStorage.removeItem("clarity_focusRemaining");
          setTimeLeft(Math.max(0, Math.ceil((toggle.whenMs - Date.now()) / 1000)));
          setPaused(false);
        }
      }
    };
    reconcile();
    document.addEventListener("visibilitychange", reconcile);
    return () => document.removeEventListener("visibilitychange", reconcile);
  }, [mode, running, paused, swRunning, swPaused]); // eslint-disable-line

  // Weekday grid labels — still used by the custom to-do editor's recurrence picker.
  const WEEKDAY_LABELS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  // Per-item completion: a habit is done once it hits its daily target; a task that's
  // done has already dropped out of the queue; a custom counts as done once it's been
  // logged as a session today.
  const todaysCustomDone = new Set(
    focusSessions.filter(s => s.date === qToday && s.kind === "custom").map(s => s.refId)
  );
  const queueItemDone = (it) => {
    if (!it) return false;
    if (it.kind === "habit") return habitDoneCount(it.ref) >= (it.ref.dailyCount || 1);
    if (it.kind === "custom") return todaysCustomDone.has(it.ref.id);
    return false; // tasks: completed ones are already filtered out of queueItems
  };
  // Counted (X/week or X/month) habits are optional extras, not part of today's
  // required due list — they don't count toward the To Do badge or "all done".
  const remainingQueueItems = queueItems.filter(it => !queueItemDone(it) && !it.counted);
  const allQueueDone = queueItems.filter(it => !it.counted).length > 0 && remainingQueueItems.length === 0;
  // Completed today — gathered independently of queueBase since finished tasks
  // are excluded from it entirely; this feeds the panel's collapsible Done section.
  const doneQueueItems = [
    ...habits
      .filter(h => !isRestrictedType(h) && (isHabitDueOn(h, qToday) || isCountedFreq(h)) && habitStarted(h) && habitDoneCount(h) >= (h.dailyCount || 1))
      .map(h => ({ qid: `h:${h.id}`, kind: "habit", ref: h, title: h.name, color: h.color || "#d4789a" })),
    ...tasks
      .filter(t => (t.frequencyType && t.frequencyType !== "once")
        ? isTaskDoneOn(t, qToday, taskOccurrenceOverrides)
        : (t.done && t.completedAt && t.completedAt.slice(0, 10) === qToday))
      .map(t => ({ qid: `t:${t.id}`, kind: "task", ref: t, title: t.title, color: t.color || "#7aa5d4" })),
    ...customTodos
      .filter(c => todaysCustomDone.has(c.id))
      .map(c => ({ qid: `c:${c.id}`, kind: "custom", ref: c, title: c.title, color: "#caa0d4" })),
  ];
  // Dynamic button label — counts only the habits/tasks still LEFT today. Once
  // everything assigned is finished, switch to a "done" label instead of stale
  // counts. The button still opens the panel so a new plan can be assigned or
  // items re-run.
  const todoLabel = allQueueDone && !remainingQueueItems.length ? "All done for today 🎉" : "Select";
  // Reopen the panel pre-selected with the current plan so it can be reordered/edited
  const editPlan = () => {
    if (plan) setQueueSelected(new Set(plan.items.map(i => i.qid)));
    // Always opens on Habits, whatever is picked now (Jade, 2026-10-11).
    setQueueTab("habits");
    queueTabDirRef.current = 0; setShowQueuePanel(true);
  };

  // Look up the live habit/task/custom-todo behind a plan snapshot's {kind, id}
  const resolveItemRef = (kind, id) => kind === "habit" ? habits.find(h => h.id === id)
    : kind === "task" ? tasks.find(t => t.id === id)
    : customTodos.find(c => c.id === id);

  // Toggle a sub-task's done state from the inline "Sub-tasks" list on the
  // main card — writes straight to the same tasks[] the Planner/details panel
  // read, so it stays in sync everywhere.
  const toggleInlineSubtask = (taskId, subId) => {
    setTasks(prev => prev.map(t => t.id === taskId
      ? { ...t, subtasks: (t.subtasks || []).map(s => s.id === subId ? { ...s, done: !s.done } : s) }
      : t));
  };

  // Drop an item out of the currently-running main-page plan (used by both
  // Remove choices below) — advances/exits the run the same way finishing the
  // last item does, instead of leaving a dangling index.
  const removeFromRunningPlan = (qid) => {
    setPlan(p => {
      if (!p) return p;
      const idx = p.items.findIndex(i => i.qid === qid);
      if (idx === -1) return p;
      const items = p.items.filter(i => i.qid !== qid);
      if (items.length === 0) return null;
      const index = idx < p.index ? p.index - 1 : Math.min(p.index, items.length - 1);
      return { ...p, items, index, doneCurrent: false, lastLog: null };
    });
  };

  // Keep an already-running (single-item) plan in sync with "Add to Clock-In"
  // changes made elsewhere (Habits tab, Planner tab, or the habit/task being
  // deleted outright) — clear the plan if its one item is no longer wanted.
  // Progress already logged is left untouched either way.
  useEffect(() => {
    if (!plan || !plan.items[0]) return;
    const i = plan.items[0];
    if (isCategoryItem(i)) {
      // A category run lasts as long as the category does; follow a rename.
      const cat = categories.find(c => c.id === i.id);
      if (!cat) { setPlan(null); rememberCategory(null); }
      else if (cat.title !== i.title || cat.color !== i.color) setPlan(p => p && { ...p, items: [{ ...p.items[0], ...categoryPlanItem(cat) }] });
      return;
    }
    const ref = resolveItemRef(i.kind, i.id);
    const stillWanted = ref && (i.kind === "custom" ? true : wantsClockIn(ref));
    if (!stillWanted) setPlan(null);
  }, [habits, tasks, customTodos, categories]); // eslint-disable-line

  // "Remove" from the main clock-in card — temporarily (today only, comes back
  // on its next scheduled day) or permanently (unchecks the shared "Add to
  // Clock-In" field on the habit/task itself, same one shown in its add/edit
  // modal — re-checking it there is what brings it back).
  const confirmRemoveTemporary = () => {
    if (!removeConfirm) return;
    const { kind, id } = removeConfirm;
    // "Just for today": uncheck Add-to-Clock-In now — so the checkbox honestly
    // reflects that the item is gone today — but stamp a restore date so it
    // auto-re-checks (and reappears on its next scheduled day) starting
    // tomorrow. Re-checking the box manually in the edit panel brings it back
    // sooner. Customs have no checkbox, so they keep the date-scoped
    // queueCanceled path instead.
    const restoreDate = fmt(new Date(Date.now() + 86400000));
    if (kind === "habit") setHabits(prev => prev.map(h => h.id === id ? { ...h, addToClockIn: false, clockInRestoreDate: restoreDate } : h));
    else if (kind === "task") setTasks(prev => prev.map(t => t.id === id ? { ...t, addToClockIn: false, clockInRestoreDate: restoreDate } : t));
    else cancelQueueItem(removeConfirm.qid, "removed");
    removeFromRunningPlan(removeConfirm.qid);
    setRemoveConfirm(null);
  };
  const confirmRemovePermanent = () => {
    if (!removeConfirm) return;
    const { kind, id } = removeConfirm;
    // Permanent: clear any leftover "just for today" restore marker too, so an
    // item removed temporarily first and then permanently doesn't quietly come
    // back tomorrow.
    if (kind === "habit") setHabits(prev => prev.map(h => h.id === id ? { ...h, addToClockIn: false, clockInRestoreDate: undefined } : h));
    else if (kind === "task") setTasks(prev => prev.map(t => t.id === id ? { ...t, addToClockIn: false, clockInRestoreDate: undefined } : t));
    else if (kind === "custom") setCustomTodos(prev => prev.filter(c => c.id !== id));
    removeFromRunningPlan(removeConfirm.qid);
    setRemoveConfirm(null);
  };

  // Elapsed minutes for the current item, from the actual timer/stopwatch.
  // (workSecs/timeLeft are already in SECONDS, not ms — dividing by 60000
  // there was a bug that rounded any realistic Timer-mode session to 0.)
  const planElapsedMins = () => mode === "stopwatch"
    ? Math.round(swElapsed / 60000)
    : Math.max(0, Math.round((workSecs - timeLeft) / 60));

  // Marks the current plan item done, logs its focus session, and stamps its
  // EXACT elapsed ms onto the item itself (savedElapsedMs) so navigating back to
  // it later can resume from precisely that point even after the shared
  // timer/stopwatch has since been reset for a different item. Shared by the
  // manual check button AND a Timer running out on its own — autoCompleted marks
  // which one, so the restart panel knows whether "continue" makes sense (a
  // Timer that ran its full course has nothing left to continue).
  const completeCurrentPlanItem = (mins, elapsedMs, autoCompleted = false) => {
    const it = planCurrent;
    if (!it || !plan || plan.doneCurrent) return;
    const sid = `s_${Date.now()}`;
    if (isCategoryItem(it)) {
      // Never "done": save the session, reset the clock, keep the category picked.
      disarmAppBlocking();
      if (mode === "stopwatch") handleSwReset(); else handleReset();
      if (mins < 1) { sonnerToast("Less than a minute. Not saved"); return; }
      setFocusSessions(p => [...p, categorySession({ item: it, minutes: mins, nowMs: Date.now(), id: sid })]);
      window.dispatchEvent(new CustomEvent("clarity:sessionComplete", { detail: { categoryLabel: it.title, label: it.title } }));
      // The title was for that session only — the next one starts blank.
      setPlan(p => p && ({ ...p, items: p.items.map(x => isCategoryItem(x) ? { ...x, sessionTitle: "" } : x), lastLog: { sessionId: sid, kind: "category", id: it.id } }));
      sonnerToast(`Saved · ${mins} min of ${it.title}`);
      return;
    }
    const lastLog = { sessionId: sid, kind: it.kind, id: it.id, deltaMins: mins, prevMinutes: 0 };
    let amountSheetOpening = false;
    // Habits/tasks/to-dos are fully repeatable within a day now (e.g. exercising
    // this morning AND this evening) — each run gets its OWN logged session with
    // its own start/end time, instead of merging into one entry and losing the
    // individual time ranges. The details panel sums these back into a single
    // "total duration" view (see groupedSessionFor / sessionsGroupKey below).
    setFocusSessions(p => [...p, {
      id: sid, date: fmt(new Date(Date.now() - mins * 60000)), minutes: mins, completedAt: new Date().toISOString(),
      startedAt: new Date(Date.now() - mins * 60000).toISOString(),
      label: `${planKindLabel(it)}: ${it.title}`, categoryLabel: catTitleFor(it.goalId), categoryId: it.goalId || null,
      kind: it.kind, refId: it.id, // links the session back to its habit/task for reflections
    }]);
    window.dispatchEvent(new CustomEvent("clarity:sessionComplete", { detail: { categoryLabel: catTitleFor(it.goalId), label: it.title } }));
    if (it.kind === "task") {
      const task = tasks.find(x => x.id === it.id);
      if (task) completeTaskNow(task);
    } else if (it.kind === "habit") {
      const h = habits.find(x => x.id === it.id);
      if (h) { lastLog.prevCount = habitDoneCount(h); bumpHabitOnce(h, mins); amountSheetOpening = isAmountHabit(h) && unitOf(h) !== "time"; } // one rep (or a Time habit's minutes), then we advance
    }
    setPlan(p => ({
      ...p,
      items: p.items.map((x, i) => i === p.index ? { ...x, savedElapsedMs: elapsedMs, autoCompleted } : x),
      doneCurrent: true, lastLog,
    }));
    disarmAppBlocking();
    // A unit habit (steps, pages, …) opens the Add amount sheet instead: the
    // "Done" pill and its pop wait until that sheet closes (Jade, 2026-10-10),
    // so the sheet is the first thing seen, not a Done behind it.
    if (!amountSheetOpening) setJustCompletedAnim(true);
  };

  const handlePlanCheck = () => {
    const it = planCurrent;
    if (!it || plan.doneCurrent) return;
    const mins = Math.max(0, planElapsedMins());
    // Pause in place instead of resetting — completing early (before a Timer's
    // duration runs out) should freeze the clock at that exact moment, not wipe
    // it, so navigating back via Previous can resume from where it was left off.
    if (mode === "stopwatch") { if (swRunning && !swPaused) handleSwStartPause(); }
    else { if (running && !paused) handleStartPause(); }
    completeCurrentPlanItem(mins, currentElapsedMs(), false);
  };

  // Un-complete a fully-done item from the panel's Done section: wipe today's
  // logged focus sessions for it (so the donut/progress drops back too) and clear
  // its done state, which lands it back in today's active plan.
  const undoDoneItem = (it) => {
    if (!it) return;
    // Remove every focus session logged for this item today (donut + minutes).
    setFocusSessions(p => p.filter(s => !(s.date === qToday && s.refId === it.ref.id)));
    if (it.kind === "task") {
      toggleTaskDoneOn(it.ref, qToday, setTasks, setTaskOccurrenceOverrides, false);
    } else if (it.kind === "habit") {
      const h = it.ref;
      if (!(h.habitType === "nottodo" || h.habitType === "limited")) {
        // Reset today's count to zero so it fully leaves the Done section.
        setHabitLogs(prev => ({ ...prev, [qToday]: { ...(prev[qToday] || {}), [h.id]: 0 } }));
      }
    }
    // custom: nothing else to clear — todaysCustomDone is derived from the
    // sessions we just removed above.
    setUndoDoneConfirm(null);
  };

  // Alternative to undoing: run this same item again from the Done section
  // without touching what's already logged — today's minutes and donut slice
  // for it stay exactly as they are, and finishing this run just adds another
  // session on top (habits/tasks/to-dos are fully repeatable within a day).
  const startAnotherSessionForDoneItem = (it) => {
    if (!it) return;
    setUndoDoneConfirm(null);
    setShowQueuePanel(false);
    setQueueSummary(null);
    const newItem = { qid: it.qid, kind: it.kind, id: it.ref.id, title: it.title, color: it.color, goalId: it.ref.goalId || null, dailyCount: it.kind === "habit" ? (it.ref.dailyCount || 1) : 1 };
    setPlan({ items: [newItem], index: 0, doneCurrent: false, lastLog: null, hidden: false });
    handleReset(); handleSwReset();
  };

  // "Just a category": pick one straight from the Assign-to-do panel. Same
  // category again just closes the panel and leaves the timer alone.
  const pickCategory = (cat) => {
    setShowQueuePanel(false);
    setQueueSelected(new Set());
    rememberCategory(cat.id);
    if (plan && plan.items[0]?.qid === `cat:${cat.id}`) return;
    setPlan({ items: [categoryPlanItem(cat)], index: 0, doneCurrent: false, lastLog: null, hidden: false });
    setPlanReview(null);
    handleReset(); handleSwReset();
  };
  const addCategoryFromPanel = () => {
    const title = (newCatTitle || "").trim();
    if (!title) return;
    const newCat = { id: `cat_${Date.now()}`, emoji: "🎯", title, color: AUTO_PALETTE[categories.length % AUTO_PALETTE.length], isGoal: false, goalDescription: "", createdAt: new Date().toISOString() };
    window.dispatchEvent(new CustomEvent("clarity:addCategory", { detail: newCat }));
    setNewCatTitle(null);
    setQueueSelected(new Set([`cat:${newCat.id}`])); // Continue confirms it, like any pick
  };

  const exitPlan = () => {
    setPlan(null);
    setPlanReview(null);
    if (mode === "stopwatch") handleSwReset(); else handleReset();
  };

  // Un-mark a plan item's completion: drop its logged focus session (so the
  // donut/minutes fall back too) and reverse the task/habit completion. Shared
  // by the reviewed-item resume flow and the Done-pill restart.
  const uncompleteItem = (it) => {
    if (!it) return;
    setFocusSessions(p => {
      let cut = -1;
      for (let i = p.length - 1; i >= 0; i--) { if (p[i].date === qToday && p[i].refId === it.id) { cut = i; break; } }
      return cut === -1 ? p : p.filter((_, i) => i !== cut);
    });
    if (it.kind === "task") {
      const task = tasks.find(x => x.id === it.id);
      if (task) toggleTaskDoneOn(task, qToday, setTasks, setTaskOccurrenceOverrides, false);
    } else if (it.kind === "habit") {
      const h = habits.find(x => x.id === it.id);
      if (h && isAmountHabit(h)) {
        // A Time habit takes back the minutes that session added; any other
        // unit's amount was typed in by hand, so it's left as entered.
        if (unitOf(h) === "time") {
          const last = [...focusSessions].reverse().find(x => x.date === qToday && x.refId === it.id);
          const back = Math.max(1, Math.round(last?.minutes || 0));
          setHabitLogs(prev => ({ ...prev, [qToday]: { ...(prev[qToday] || {}), [h.id]: addToLog(h, prev[qToday]?.[h.id], -back) } }));
        }
      } else if (h && !(h.habitType === "nottodo" || h.habitType === "limited")) {
        const target = h.dailyCount || 1;
        setHabitLogs(prev => { const cur = prev[qToday]?.[h.id]; const n = typeof cur === "number" ? cur : (cur ? target : 0); return { ...prev, [qToday]: { ...(prev[qToday] || {}), [h.id]: Math.max(0, n - 1) } }; });
      }
    }
  };

  // Restore the shared timer/stopwatch to a plan item's saved position, PAUSED.
  // No snapshot (a fresh item never navigated to) → a clean reset instead.
  const restorePausedForItem = (it) => {
    const savedMs = it?.savedElapsedMs;
    if (savedMs == null) { if (mode === "stopwatch") handleSwReset(); else handleReset(); return; }
    if (mode === "stopwatch") restoreStopwatchPaused(savedMs);
    else restoreTimerPaused(Math.max(0, workSecs * 1000 - savedMs), workSecs * 1000);
  };

  // ── Previous / review navigation: step back to an earlier item ──────────────
  // Tapping Previous jumps straight back to that item and restores its exact
  // saved position, PAUSED — no confirmation dialog. It stays marked done (in the
  // Done section / donut) until the user actually presses play to resume it.
  const reviewFrom = () => (planReview !== null ? planReview : (plan ? plan.index : 0));
  const goPrevPlan = () => {
    if (!plan) return;
    const running_ = mode === "stopwatch" ? swRunning : running;
    const paused_ = mode === "stopwatch" ? swPaused : paused;
    // Leaving the current (non-reviewed) item mid-session → remember where it was
    // so a later Forward can bring it back instead of losing the progress.
    if (planReview === null && !plan.doneCurrent && (running_ || paused_)) {
      const ms = currentElapsedMs();
      setPlan(p => p ? { ...p, items: p.items.map((x, i) => i === p.index ? { ...x, savedElapsedMs: ms } : x) } : p);
    }
    if (running_ && !paused_) { if (mode === "stopwatch") handleSwStartPause(); else handleStartPause(); }
    const targetIdx = Math.max(0, reviewFrom() - 1);
    setPlanReview(targetIdx);
    restorePausedForItem(plan.items[targetIdx]);
  };
  const goForwardReview = () => {
    if (planReview === null || !plan) return;
    const ni = planReview + 1;
    if (ni >= plan.index) {
      setPlanReview(null);
      restorePausedForItem(plan.items[plan.index]);
    } else {
      setPlanReview(ni);
      restorePausedForItem(plan.items[ni]);
    }
  };

  // Restart a reviewed or just-completed item — reached from both the review
  // state's "Restart" button (via Previous) and the Done pill. keepTime → resume
  // paused exactly where it left off; otherwise wipe to 00:00.
  const restartReviewedPlan = (keepTime = false) => {
    const targetIdx = planReview !== null ? planReview : (plan ? plan.index : 0);
    const it = plan ? plan.items[targetIdx] : null;
    if (!it) { setRestartConfirm(null); return; }
    uncompleteItem(it);
    setPlanReview(null);
    setPlan(p => ({ ...p, index: targetIdx, doneCurrent: false, lastLog: null }));
    if (keepTime) {
      const savedMs = it.savedElapsedMs ?? 0;
      if (mode === "stopwatch") restoreStopwatchPaused(savedMs);
      else restoreTimerPaused(Math.max(0, workSecs * 1000 - savedMs), workSecs * 1000);
    } else {
      if (mode === "stopwatch") handleSwReset(); else handleReset();
    }
    setRestartConfirm(null);
  };

  // ── Sortable queue card ─────────────────────────────────────────────────────
  const QueueCard = ({ it }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: it.qid });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 5 : "auto" };
    const expanded = expandedQueueId === it.qid;
    const selected = queueSelected.has(it.qid);
    const target = it.kind === "habit" ? (it.ref.dailyCount || 1) : 1;
    const done = it.kind === "habit" ? habitDoneCount(it.ref) : 0;
    return (
      <div ref={setNodeRef} style={style}>
        <div style={{
          display: "flex", alignItems: "center", gap: 8, padding: "11px 12px",
          background: selected ? CQ_PICKED_BG : "var(--c-bg-card)", borderRadius: 14,
          border: "none",
          boxShadow: isDragging ? "0 6px 18px rgba(0,0,0,0.14)" : CQ_CARD_SHADOW, transition: "background 0.2s, box-shadow 0.2s",
        }}>
          {/* drag handle */}
          <span {...attributes} {...listeners} title="Drag to reorder"
            style={{ cursor: "grab", touchAction: "none", color: "var(--c-text-soft)", display: "flex", flexShrink: 0 }}>
            <GripVertical size={17} />
          </span>
          {/* icon + title — tap to select. Single pick only: the selected state
              always uses the theme accent color, not the item's own color, so
              it's unambiguous which one card is actually picked. */}
          <button onClick={() => toggleQueueSelect(it.qid)} title="Tap to select"
            style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 9, background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left", padding: 0 }}>
            <span style={{ width: 28, height: 28, borderRadius: 8, background: selected ? "var(--c-primary)" : `${it.color}22`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: selected ? "#fff" : it.color, transition: "all 0.15s" }}>
              {selected ? <Check size={16} strokeWidth={3} /> : it.kind === "habit" ? <HabitIcon h={it.ref} size={15} /> : it.kind === "task" ? <ListTodo size={15} /> : it.ref.emoji ? <span style={{ fontSize: 14 }}>{it.ref.emoji}</span> : <Sparkles size={15} />}
            </span>
            <span style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 700, color: "var(--c-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {it.title}
              {it.kind === "habit" && (target > 1 || isAmountHabit(it.ref)) && (
                <span style={{ fontSize: 10, fontWeight: 800, color: "var(--c-text-accent)", marginLeft: 6 }}>{isAmountHabit(it.ref) ? formatProgress(it.ref, done) : `${done}/${target}`}</span>
              )}
            </span>
          </button>
          {/* dropdown arrow — tap to expand edit/remove/skip */}
          <button onClick={() => setExpandedQueueId(expanded ? null : it.qid)} title="Options"
            style={{ flexShrink: 0, background: "transparent", border: "none", cursor: "pointer", padding: 4, display: "flex", alignItems: "center" }}>
            {expanded ? <ChevronUp size={17} style={{ color: "var(--c-text-soft)" }} /> : <ChevronDown size={17} style={{ color: "var(--c-text-soft)" }} />}
          </button>
        </div>
        {expanded && (
          <div style={{ display: "flex", gap: 6, padding: "8px 12px 2px 12px" }}>
            {it.kind === "custom" ? (
              <button onClick={() => openEditCustomTodo(it.ref)} title="Edit" style={{ ...S.qIconBtn, flex: 1, justifyContent: "center" }}><Pencil size={13} /> Edit</button>
            ) : (
              <button onClick={() => editQueueItem(it)} title="Edit" style={{ ...S.qIconBtn, flex: 1, justifyContent: "center" }}><Pencil size={13} /> Edit</button>
            )}
            <button onClick={() => setViewDetailsFor({ qid: it.qid, kind: it.kind, id: it.ref.id, title: it.title })} title="View details"
              style={{ ...S.qIconBtn, flex: 1, justifyContent: "center" }}><Info size={13} /> Details</button>
            <button onClick={() => setRemoveConfirm({ qid: it.qid, kind: it.kind, id: it.ref.id, title: it.title })} title="Remove from list"
              style={{ ...S.qIconBtn, flex: 1, justifyContent: "center" }}><Minus size={15} /> Remove</button>
          </div>
        )}
      </div>
    );
  };

  // Empty drop target for the Counted section — lets a promoted counted habit be
  // dragged back down when no counted cards remain to drop it onto.
  const CountedDropZone = () => {
    const { setNodeRef, isOver } = useDroppable({ id: "counted-zone" });
    return (
      <div ref={setNodeRef} style={{
        border: `2px dashed ${isOver ? "var(--c-primary)" : "var(--c-border-mid)"}`,
        background: isOver ? "var(--c-primary)18" : "transparent",
        borderRadius: 14, padding: "16px 12px", textAlign: "center",
        fontSize: 12, fontWeight: 600, color: "var(--c-text-soft)", transition: "all 0.15s",
      }}>
        Drag a flexible habit here to move it back
      </div>
    );
  };

  // ── Bottom sheet: pick what this session is for ────────────────────────────
  // No header: three section tabs — Habits / Tasks / Categories — each showing
  // only its own list, in a segmented switcher (Jade, 2026-10-15; it was an
  // underlined row). Every row is a borderless card with a soft shadow on the
  // tinted sheet, categories included (they were lined rows).
  const queueListStyle = { display: "flex", flexDirection: "column", gap: 8 };
  const QueuePanel = () => {
    const runnable = queueSelected.size; // 0 or 1 — single pick only
    const doneQidSet = new Set(doneQueueItems.map(it => it.qid));
    const mainItems = groupHabitsFirst(queueItems.filter(it => !it.counted && !doneQidSet.has(it.qid)));
    const habitMain = mainItems.filter(it => it.kind === "habit");
    const taskMain = mainItems.filter(it => it.kind !== "habit");
    const countedItemsView = queueItems.filter(it => it.counted && !doneQidSet.has(it.qid));
    // Every counted habit for today (in-section OR dragged up into the run). The
    // section stays visible whenever the user has any, so a promoted habit can
    // always be dragged back — even when the section itself is momentarily empty.
    const hasCounted = queueItems.some(it => it.countedByNature && !doneQidSet.has(it.qid));
    const tab = queueTab;
    const tabDone = doneQueueItems.filter(it => tab === "habits" ? it.kind === "habit" : it.kind !== "habit");
    const TABS = [["habits", "Habits"], ["tasks", "Tasks"], ["categories", "Categories"]];
    const emptyNote = (title, sub) => (
      <div style={{ textAlign: "center", padding: "28px 12px" }}>
        <div style={{ fontSize: 30, marginBottom: 6 }}>🌙</div>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "var(--c-text)" }}>{title}</p>
        {sub && <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--c-text-soft)" }}>{sub}</p>}
      </div>
    );
    const tabIdx = Math.max(0, CQ_TABS.indexOf(tab));
    const switchTab = (id) => {
      if (id === tab) return;
      queueTabDirRef.current = CQ_TABS.indexOf(id) > tabIdx ? 1 : -1;
      setQueueTab(id);
    };
    return (
      <div className="no-nav-blur" style={{ position: "fixed", inset: 0, zIndex: 10000, display: "flex", alignItems: "flex-end", justifyContent: "center", fontFamily: "'Quicksand','Nunito',sans-serif" }}>
        {/* Bottom sheet (Jade, 2026-10-15; it was a centred panel that just appeared). */}
        <div ref={queueSheet.dimRef} style={{ position: "absolute", inset: 0, background: "rgba(40,20,35,0.5)" }} onClick={queueSheet.dismiss} />
        <div ref={queueSheet.sheetRef} className="cq-sheet" data-tour="assign-todo-panel" style={{ position: "relative", background: CQ_SHEET_BG, borderRadius: "28px 28px 0 0", maxWidth: 520, width: "100%", height: "min(640px, 86vh)", display: "flex", flexDirection: "column", boxShadow: "0 -8px 32px rgba(0,0,0,0.16)", overflow: "hidden" }}>
          {/* handle */}
          <div aria-hidden style={{ width: 40, height: 5, borderRadius: 99, background: "rgba(120,120,128,0.3)", margin: "10px auto 0", flexShrink: 0 }} />
          {/* section tabs — a segmented switcher whose white pill slides to the
              picked tab (same look as the habit form's To Do / Restricted). */}
          <div style={{ padding: "12px 16px 12px", display: "flex", alignItems: "center", flexShrink: 0 }}>
            <div role="tablist" style={{ position: "relative", flex: 1, display: "flex", padding: 4, borderRadius: 14, background: "rgba(120,120,128,0.12)" }}>
              <div aria-hidden style={{
                position: "absolute", top: 4, bottom: 4, left: 4, width: "calc((100% - 8px) / 3)", borderRadius: 10,
                background: "var(--c-bg-card)", boxShadow: "0 2px 8px rgba(0,0,0,0.10), 0 1px 2px rgba(0,0,0,0.06)",
                transform: `translateX(${tabIdx * 100}%)`, transition: "transform 0.28s cubic-bezier(0.22,1,0.36,1)",
              }} />
              {TABS.map(([id, label]) => (
                <button key={id} role="tab" onClick={() => switchTab(id)} aria-selected={tab === id}
                  data-tour={id === "tasks" ? "clockin-tasks-tab" : undefined}
                  style={{
                    position: "relative", zIndex: 1, flex: 1, background: "none", border: "none", padding: "8px 0", minHeight: 0, cursor: "pointer", fontFamily: "inherit",
                    fontSize: 14.5, fontWeight: tab === id ? 800 : 700, color: tab === id ? "var(--c-text-accent)" : "var(--c-text-soft)", transition: "color 0.2s",
                  }}>{label}</button>
              ))}
            </div>
          </div>
          {/* scrollable list — re-keyed per tab so it slides in from the side moved to */}
          <div key={tab} className={"cq-list" + (queueTabDirRef.current > 0 ? " cq-tab-r" : queueTabDirRef.current < 0 ? " cq-tab-l" : "")} style={{ flex: 1, overflowY: "auto", padding: "4px 16px 12px" }}>
            {/* None — clear any assignment and just run the timer/stopwatch on its
                own, back to the plain Reset/Start/Done layout (no Check pill). */}
            <button onClick={() => { rememberCategory(null); exitPlan(); setQueueSelected(new Set()); setShowQueuePanel(false); }}
              style={{
                display: "flex", alignItems: "center", gap: 9, width: "100%", marginBottom: 10,
                padding: "11px 12px", borderRadius: 14, border: "none",
                background: "var(--c-bg-card)", boxShadow: CQ_CARD_SHADOW, cursor: "pointer", fontFamily: "inherit", textAlign: "left",
              }}>
              <span style={{ width: 28, height: 28, borderRadius: 8, background: "var(--c-bg-soft)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "var(--c-text-soft)" }}>
                <X size={15} />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 14.5, fontWeight: 700, color: "var(--c-text)" }}>None</span>
              </span>
            </button>

            {tab === "tasks" && <>{/* add custom to-do — right under None (Jade, 2026-10-11) — opens the full editor (title, description, emoji,
                    recurrence, category) in a modal */}
                <button data-tour="clockin-custom-todo-btn" onClick={openNewCustomTodo}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%", marginBottom: 10, padding: "10px 14px", borderRadius: 12, border: "1.5px dashed var(--c-border-mid)", background: "transparent", color: "var(--c-text-soft)", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700 }}>
                  <Plus size={15} /> Add a custom to-do
                </button></>}
            {tab === "categories" ? (
              <div style={queueListStyle}>
                {categories.length === 0 && newCatTitle === null && emptyNote("No categories yet", "Make one below.")}
                {categories.map((cat, i) => {
                  const on = queueSelected.has(`cat:${cat.id}`);
                  return (
                    <button key={cat.id} type="button" onClick={() => toggleQueueSelect(`cat:${cat.id}`)}
                      style={{
                        display: "flex", alignItems: "center", gap: 10, width: "100%", minHeight: 0,
                        padding: "11px 12px", background: on ? CQ_PICKED_BG : "var(--c-bg-card)",
                        border: "none", borderRadius: 14, boxShadow: CQ_CARD_SHADOW,
                        cursor: "pointer", fontFamily: "inherit", textAlign: "left", transition: "background 0.2s",
                      }}>
                      {/* Icon rebuilt at a vivid, mid lightness so pale category colours still
                          read clearly (Jade, 2026-10-11), on a tile of the same hue. */}
                      <span style={{ width: 28, height: 28, borderRadius: 8, background: `color-mix(in srgb, ${vividPastel(cat.color || "#d4789a", 0.7, 0.5)} 18%, transparent)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <CategoryIcon cat={cat} size={16} color={vividPastel(cat.color || "#d4789a", 0.7, 0.5)} />
                      </span>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 700, color: "var(--c-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cat.title}</span>
                      {on && <Check size={17} strokeWidth={3} color="var(--c-text-accent)" />}
                    </button>
                  );
                })}
                {newCatTitle === null ? (
                  <button type="button" onClick={() => setNewCatTitle("")}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%", marginTop: 4, padding: "10px 14px", minHeight: 0, borderRadius: 12, border: "1.5px dashed var(--c-border-mid)", background: "transparent", cursor: "pointer", fontFamily: "inherit", fontSize: 13.5, fontWeight: 700, color: "var(--c-text-soft)" }}>
                    <Plus size={15} /> New category
                  </button>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 4 }}>
                    <input autoFocus value={newCatTitle} maxLength={40} placeholder="Category name"
                      onChange={e => setNewCatTitle(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") addCategoryFromPanel(); if (e.key === "Escape") setNewCatTitle(null); }}
                      style={{ ...S.input, flex: 1, minWidth: 0, fontSize: 16, padding: "8px 10px" }} />
                    <button onClick={addCategoryFromPanel} disabled={!newCatTitle.trim()}
                      style={{ ...S.btn, padding: "8px 14px", minHeight: 0, fontWeight: 800, color: "var(--c-text-accent)", borderColor: "var(--c-primary)", opacity: newCatTitle.trim() ? 1 : 0.5 }}>Add</button>
                    <button onClick={() => setNewCatTitle(null)} aria-label="Cancel"
                      style={{ background: "none", border: "none", cursor: "pointer", color: "var(--c-text-soft)", padding: 4, display: "flex", minHeight: 0 }}><X size={16} /></button>
                  </div>
                )}
              </div>
            ) : (
            <DndContext sensors={qDnDSensors} collisionDetection={closestCenter} onDragEnd={handleQueueDragEnd}>
              <SortableContext items={tab === "habits" ? [...habitMain.map(i => i.qid), ...(showCountedSection ? countedItemsView.map(i => i.qid) : [])] : taskMain.map(i => i.qid)} strategy={verticalListSortingStrategy}>
                {tab === "habits" ? (
                  habitMain.length === 0 && !hasCounted ? emptyNote("No habits for today") : (
                    <div style={queueListStyle}>
                      {habitMain.map(it => <QueueCard key={it.qid} it={it} />)}
                    </div>
                  )
                ) : (
                  taskMain.length === 0 ? emptyNote("No tasks for today", "Add a custom to-do above.") : (
                    <div style={queueListStyle}>
                      {taskMain.map(it => <QueueCard key={it.qid} it={it} />)}
                    </div>
                  )
                )}



                {/* counted habits — optional extras (X times a week/month). Drag one up into
                    the list above to add it to today's run; drag it back down to remove it.
                    Stays visible as long as any counted habit exists (even if all are
                    currently up in the run), so they can always be dragged back. */}
                {tab === "habits" && hasCounted && (
                  <div style={{ marginTop: 14 }}>
                    <button onClick={() => setShowCountedSection(v => !v)}
                      style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit", color: "var(--c-text-soft)", fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", padding: "4px 0" }}>
                      {showCountedSection ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Flexible ({countedItemsView.length})
                    </button>
                    {showCountedSection && (
                      <div style={{ ...queueListStyle, marginTop: 4 }}>
                        {countedItemsView.map(it => <QueueCard key={it.qid} it={it} />)}
                        {countedItemsView.length === 0 && <CountedDropZone />}
                      </div>
                    )}
                  </div>
                )}
              </SortableContext>
            </DndContext>
            )}

            {/* done today — completed via the timer/stopwatch, tucked away so the active list stays clean */}
            {tab !== "categories" && tabDone.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <button onClick={(e) => {
                    const opening = !showDoneSection;
                    const wrap = e.currentTarget.parentElement;
                    setShowDoneSection(opening);
                    // Opening Done scrolls its rows into view, so they don't sit unseen
                    // below the fold (Jade, 2026-10-16).
                    if (opening) requestAnimationFrame(() => requestAnimationFrame(() => wrap?.scrollIntoView({ behavior: "smooth", block: "nearest" })));
                  }}
                  style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit", color: "var(--c-text-soft)", fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", padding: "2px 0" }}>
                  {showDoneSection ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Done ({tabDone.length})
                </button>
                {showDoneSection && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
                    {tabDone.map(it => (
                      <button key={it.qid} onClick={() => setUndoDoneConfirm(it)} title="Tap to undo"
                        style={{ display: "flex", alignItems: "center", gap: 9, padding: "9px 12px", background: "var(--c-bg-card)", borderRadius: 12, border: "none", boxShadow: CQ_CARD_SHADOW, opacity: 0.85, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                        <span style={{ fontSize: 12.5, flex: 1, color: "var(--c-text-mid)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.title}</span>
                        <RotateCcw size={13} style={{ color: "var(--c-text-soft)", flexShrink: 0 }} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* footer: Continue — confirms the pick on every tab (Jade). A full-width
              button pinned to the sheet's bottom edge, the usual home for a sheet's
              one main action (Jade, 2026-10-15; it was a small centred pill). The
              soft fade above it lets the list scroll away under it cleanly. */}
          <div style={{ position: "relative", padding: "8px 16px calc(16px + env(safe-area-inset-bottom, 0px))", flexShrink: 0 }}>
            <div aria-hidden style={{ position: "absolute", left: 0, right: 0, bottom: "100%", height: 24, background: `linear-gradient(to bottom, transparent, ${CQ_SHEET_BG})`, pointerEvents: "none" }} />
            <button onClick={startQueue} disabled={runnable === 0} className="clarity-add-btn"
              style={{ ...S.addBtn, width: "100%", justifyContent: "center", padding: "15px 18px", borderRadius: 16, fontSize: 16, opacity: runnable === 0 ? 0.5 : 1, cursor: runnable === 0 ? "default" : "pointer", transition: "opacity 0.2s" }}>
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Gentle runner page ──────────────────────────────────────────────────────
  const RunnerPage = () => {
    const it = runnerCurrent;
    if (!it) return null;
    const total = runner.ids.length;
    const pos = runner.index + 1;
    const target = it.kind === "habit" ? (it.ref.dailyCount || 1) : 1;
    const done = it.kind === "habit" ? Math.max(runner.counts[it.qid] || 0, habitDoneCount(it.ref)) : 0;
    const elapsedSec = runner.active ? Math.floor((runnerNow - runner.itemStartedAt) / 1000) : 0;
    const isStopwatch = mode === "stopwatch";
    const dispSec = isStopwatch ? elapsedSec : Math.max(0, workSecs - elapsedSec);
    const em = Math.floor(dispSec / 60), es = dispSec % 60;
    const kindLabel = it.kind === "habit" ? "Habit" : it.kind === "task" ? "Task" : "To-do";
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 10001, background: "var(--c-bg)", display: "flex", flexDirection: "column" }}>
        {/* top bar: progress + exit */}
        <div style={{ padding: "16px 18px 0", display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={pauseRunner} title="Pause & resume later" style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--c-text-soft)", padding: 4 }}><X size={22} /></button>
          <div style={{ flex: 1 }}>
            <div style={{ height: 7, borderRadius: 99, background: "var(--c-bg-soft)", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${(runner.index / total) * 100}%`, background: "linear-gradient(90deg, var(--c-primary-light), var(--c-primary-deep))", borderRadius: 99, transition: "width 0.5s cubic-bezier(.2,.8,.2,1)" }} />
            </div>
            <p style={{ margin: "5px 0 0", fontSize: 11, fontWeight: 700, color: "var(--c-text-soft)", textAlign: "center" }}>{pos} of {total}</p>
          </div>
          <button onClick={() => setRunner(r => ({ ...r, showTimer: !r.showTimer }))} title="Toggle timer"
            style={{ background: "transparent", border: "none", cursor: "pointer", color: runner.showTimer ? "var(--c-primary-deep)" : "var(--c-text-soft)", padding: 4 }}>
            {runner.showTimer ? <Timer size={20} /> : <Timer size={20} style={{ opacity: 0.5 }} />}
          </button>
        </div>

        {/* center: the calm view */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, padding: "20px 28px", textAlign: "center" }}>
          <span style={{ width: 64, height: 64, borderRadius: 18, background: `${it.color}22`, display: "flex", alignItems: "center", justifyContent: "center", color: it.color }}>
            {it.kind === "habit" ? <HabitIcon h={it.ref} size={32} /> : it.kind === "task" ? <ListTodo size={32} /> : <Sparkles size={32} />}
          </span>
          <div>
            <p style={{ margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "var(--c-text-soft)" }}>{kindLabel}</p>
            <h2 style={{ margin: "6px 0 0", fontSize: 26, fontWeight: 800, color: "var(--c-text)", lineHeight: 1.25 }}>{it.title}</h2>
            {it.kind === "habit" && isAmountHabit(it.ref)
              ? <p style={{ margin: "8px 0 0", fontSize: 13, fontWeight: 700, color: it.color }}>{formatProgress(it.ref, done)} today</p>
              : target > 1 && <p style={{ margin: "8px 0 0", fontSize: 13, fontWeight: 700, color: it.color }}>{done}/{target} done today</p>}
          </div>
          {runner.showTimer && (
            <div style={{ textAlign: "center" }}>
              <p style={{ margin: 0, fontSize: 34, fontWeight: 700, fontFamily: "Quicksand", color: "var(--c-text-accent)", letterSpacing: -1 }}>
                {String(em).padStart(2, "0")}:{String(es).padStart(2, "0")}
              </p>
              <p style={{ margin: "2px 0 0", fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "var(--c-text-soft)" }}>
                {isStopwatch ? "elapsed" : "remaining"}
              </p>
            </div>
          )}
          {runnerNext && <p style={{ margin: 0, fontSize: 12, color: "var(--c-text-soft)" }}>next: {runnerNext.title}</p>}
        </div>

        {/* bottom: check */}
        <div style={{ padding: "0 28px 36px", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
          {!runner.active ? (
            <button onClick={resumeRunner} className="clarity-add-btn" style={{ ...S.addBtn, padding: "14px 40px", fontSize: 15 }}>
              <PlayCircle size={20} /> Resume
            </button>
          ) : (
            <button onClick={handleRunnerCheck}
              style={{ width: 76, height: 76, borderRadius: "50%", border: "none", cursor: "pointer",
                background: "linear-gradient(135deg, #7bc67e, #4a9e5c)", color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 22px rgba(74,158,92,0.4)" }}>
              <Check size={34} strokeWidth={3} />
            </button>
          )}
          <p style={{ margin: 0, fontSize: 12, color: "var(--c-text-soft)" }}>{runner.active ? "Mark as complete" : "Paused"}</p>
        </div>
      </div>
    );
  };

  // ── Continue / break checkpoint between items ───────────────────────────────
  const QueueDecisionModal = () => (
    <div style={{ position: "fixed", inset: 0, zIndex: 10002, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div className="clarity-fade" style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }} />
      <div className="clarity-pop" style={{ position: "relative", background: "var(--c-bg-card)", borderRadius: 22, padding: "26px 24px 20px", maxWidth: 320, width: "100%", textAlign: "center", boxShadow: "0 10px 44px rgba(0,0,0,0.22)" }}>
        <div style={{ fontSize: 30, marginBottom: 8 }}>{queueDecision.mode === "break" ? "☕" : "✨"}</div>
        <h3 style={{ margin: "0 0 4px", fontSize: 17, color: "var(--c-text-accent)", fontWeight: 800 }}>
          {queueDecision.mode === "break" ? "Take a breather" : "Nice work!"}
        </h3>
        <p style={{ margin: "0 0 18px", fontSize: 13, color: "var(--c-text-soft)" }}>Up next: <b style={{ color: "var(--c-text)" }}>{queueDecision.nextLabel}</b></p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <button onClick={resumeFromDecision} className="clarity-add-btn" style={{ ...S.addBtn, width: "100%", justifyContent: "center" }}>
            <PlayCircle size={17} /> Continue
          </button>
          <button onClick={pauseRunner} style={{ ...S.btn, width: "100%" }}>
            <PauseCircle size={15} /> Pause &amp; resume later
          </button>
        </div>
      </div>
    </div>
  );

  // ── End-of-queue celebration ────────────────────────────────────────────────
  const QueueSummaryModal = () => (
    <div style={{ position: "fixed", inset: 0, zIndex: 10002, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "'Quicksand','Nunito',sans-serif" }}>
      <div className="clarity-fade" style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }} onClick={() => setQueueSummary(null)} />
      <div className="clarity-pop" style={{ position: "relative", background: "var(--c-bg-card)", borderRadius: 22, padding: "30px 26px 22px", maxWidth: 300, width: "100%", textAlign: "center", boxShadow: "0 10px 44px rgba(0,0,0,0.22)" }}>
        <div style={{ fontSize: 40, marginBottom: 6 }}>🎉</div>
        <h3 style={{ margin: "0 0 6px", fontSize: 19, color: "var(--c-text-accent)", fontWeight: 800 }}>Queue complete!</h3>
        <p style={{ margin: "0 0 18px", fontSize: 14, color: "var(--c-text)" }}>
          {queueSummary.count} done · {queueSummary.mins >= 60 ? `${Math.floor(queueSummary.mins / 60)}h ` : ""}{queueSummary.mins % 60}m focused
        </p>
        <button onClick={() => setQueueSummary(null)} className="clarity-add-btn" style={{ ...S.addBtn, width: "100%", justifyContent: "center" }}>Done</button>
      </div>
    </div>
  );

  // Confirms the mid-run "Restart" icon tap (timer/stopwatch still active, not
  // yet completed) before wiping it to 00:00 — a plain safety check, since
  // there's no saved/completed snapshot to "continue" from at this point.
  const confirmQuickReset = () => {
    if (mode === "stopwatch") handleSwReset(); else handleReset();
    setQuickResetConfirm(false);
  };

  // Switching between Custom (timer) and Elapsed (stopwatch) mid-run would silently
  // discard whichever one was active — ask first, then reset it to 00:00 before
  // switching so the new mode always starts clean.
  const requestModeSwitch = (id) => {
    if (id === mode) return;
    // Only confirm while actively counting — once paused or already marked
    // done (check button pressed), there's nothing left to lose by switching.
    const isActive = mode === "stopwatch" ? (swRunning && !swPaused) : (running && !paused);
    if (isActive) { setModeSwitchConfirm(id); return; }
    setMode(id); save("clarity_focusMode", id);
  };
  const confirmModeSwitch = () => {
    const id = modeSwitchConfirm;
    if (mode === "stopwatch") handleSwReset(); else handleReset();
    setMode(id); save("clarity_focusMode", id);
    setModeSwitchConfirm(null);
  };
  const ModeSwitchConfirmModal = () => (
    <div style={{ ...CONFIRM_OVERLAY, zIndex: 10054 }}>
      <div className="clarity-fade" style={CONFIRM_DIM} onClick={() => setModeSwitchConfirm(null)} />
      <div className="clarity-pop" style={CONFIRM_CARD}>
        <ConfirmHeader tone="accent" icon={ArrowLeftRight}
          title={`Switch to ${modeSwitchConfirm === "stopwatch" ? "Elapsed" : "Custom"}?`}
          subtitle={`Your current ${mode === "stopwatch" ? "stopwatch" : "timer"} progress will be reset.`} />
        <div style={CONFIRM_STACK}>
          <button onClick={() => setModeSwitchConfirm(null)} style={CONFIRM_CANCEL}>Cancel</button>
          <button onClick={confirmModeSwitch} style={CONFIRM_PRIMARY}>Switch</button>
        </div>
      </div>
    </div>
  );
  // No pop-in or backdrop fade on either restart panel (Jade, 2026-10-21).
  const QuickResetConfirmModal = () => (
    <div style={{ ...CONFIRM_OVERLAY, zIndex: 10054 }}>
      <div style={CONFIRM_DIM} onClick={() => setQuickResetConfirm(false)} />
      <div style={CONFIRM_CARD}>
        <ConfirmHeader tone="accent" icon={RotateCcw} title="Restart from 00:00?"
          subtitle={`This resets the ${mode === "stopwatch" ? "stopwatch" : "timer"} back to 00:00.`} />
        <div style={CONFIRM_STACK}>
          <button onClick={() => setQuickResetConfirm(false)} style={CONFIRM_CANCEL}>Cancel</button>
          <button onClick={confirmQuickReset} style={CONFIRM_PRIMARY}>Restart</button>
        </div>
      </div>
    </div>
  );

  // Confirms the mid-run pencil (edit duration) tap on the custom timer —
  // jumping back to the drag-to-set-duration ring calls handleReset() under
  // the hood, which wipes the running/paused progress with no way back.
  const confirmEditDuration = () => {
    handleReset();
    setEditDurationConfirm(false);
  };
  const EditDurationConfirmModal = () => (
    <div style={{ ...CONFIRM_OVERLAY, zIndex: 10054 }}>
      <div className="clarity-fade" style={CONFIRM_DIM} onClick={() => setEditDurationConfirm(false)} />
      <div className="clarity-pop" style={CONFIRM_CARD}>
        <ConfirmHeader tone="accent" icon={Pencil} title="Edit duration?" subtitle="You'll lose your progress on this timer." />
        <div style={CONFIRM_STACK}>
          <button onClick={() => setEditDurationConfirm(false)} style={CONFIRM_CANCEL}>Cancel</button>
          <button onClick={confirmEditDuration} style={CONFIRM_PRIMARY}>Edit</button>
        </div>
      </div>
    </div>
  );

  // ── Restart confirmation (mirrors the widget: keep the time, or start over) ──
  const RestartConfirmModal = () => (
    <div style={{ ...CONFIRM_OVERLAY, zIndex: 10054 }}>
      <div style={CONFIRM_DIM} onClick={() => setRestartConfirm(null)} />
      <div style={CONFIRM_CARD}>
        <ConfirmHeader tone="accent" icon={RotateCcw} title={`Restart "${restartConfirm?.title}"?`}
          subtitle={restartConfirm?.autoCompleted
            ? "It will be marked not done again and its timer reset."
            : "It will be marked not done again. Keep your time, or start over?"} />
        <div style={CONFIRM_COLUMN}>
          {!restartConfirm?.autoCompleted && (
            <button onClick={() => restartReviewedPlan(true)} style={CONFIRM_PRIMARY}>Keep the time (paused)</button>
          )}
          <button onClick={() => restartReviewedPlan(false)} style={restartConfirm?.autoCompleted ? CONFIRM_PRIMARY : CONFIRM_CANCEL}>Restart from 00:00</button>
          <button onClick={() => setRestartConfirm(null)} style={CONFIRM_CANCEL}>Cancel</button>
        </div>
      </div>
    </div>
  );

  // ── Undo (or run again) a completed item from the Done section ─────────────
  const UndoDoneConfirmModal = () => (
    <div style={{ ...CONFIRM_OVERLAY, zIndex: 10054 }}>
      <div className="clarity-fade" style={CONFIRM_DIM} onClick={() => setUndoDoneConfirm(null)} />
      <div className="clarity-pop" style={CONFIRM_CARD}>
        <ConfirmHeader tone="accent" icon={CheckCircle} title={`"${undoDoneConfirm?.title}"`}
          subtitle="Already done for today. What would you like to do?" />
        <div style={CONFIRM_COLUMN}>
          <button onClick={() => startAnotherSessionForDoneItem(undoDoneConfirm)} style={CONFIRM_PRIMARY}>
            <PlayCircle size={16} /> Do it again
          </button>
          <button onClick={() => undoDoneItem(undoDoneConfirm)} style={CONFIRM_CANCEL}><Undo2 size={16} /> Undo</button>
          <button onClick={() => setUndoDoneConfirm(null)} style={CONFIRM_CANCEL}>Cancel</button>
        </div>
      </div>
    </div>
  );

  // ── Remove confirmation: today-only vs permanent (unchecks the shared
  //    "Add to Clock-In" field on the habit/task itself) ───────────────────────
  // Pick an option, then Confirm (Jade, 2026-10-11: a tap used to act at once
  // and the panel vanished with the item). The pick lives on removeConfirm
  // itself (`choice`), so each opening starts with nothing picked. No pop-in
  // or backdrop fade, like the other confirm panels.
  const RemoveConfirmModal = () => {
    const isCustom = removeConfirm?.kind === "custom";
    const choice = removeConfirm?.choice || null;
    const pick = (c) => setRemoveConfirm(p => p && ({ ...p, choice: c }));
    // Kept deliberately plain (Jade: "looks like a lot"): no subtitle, no
    // outlined cards; the picked row just gets a soft fill.
    const options = [
      { key: "today", title: "Just for today", desc: "Back on its next scheduled day" },
      { key: "permanent", title: isCustom ? "Delete it" : "Permanently",
        desc: isCustom ? "This to-do is deleted" : "Turn it back on from its edit screen" },
    ];
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 10054, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "'Quicksand','Nunito',sans-serif" }}>
        <div style={CONFIRM_DIM} onClick={() => setRemoveConfirm(null)} />
        <div style={{ ...CONFIRM_CARD, padding: "26px 16px 18px" }}>
          <ConfirmHeader icon={MinusCircle} title={`Remove "${removeConfirm?.title}"?`} subtitle="" />
          <div role="radiogroup" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {options.map(o => {
              const on = choice === o.key;
              return (
                <button key={o.key} role="radio" aria-checked={on} onClick={() => pick(o.key)}
                  style={{ width: "100%", minHeight: 0, padding: "10px 10px", borderRadius: 12, border: "none", background: on ? "var(--c-bg-active)" : "transparent",
                    fontFamily: "inherit", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 12, transition: "background 0.15s" }}>
                  <span aria-hidden style={{ flexShrink: 0, width: 18, height: 18, borderRadius: "50%", boxSizing: "border-box",
                    border: on ? "5px solid var(--c-primary)" : "1.5px solid var(--c-border-mid)", background: "var(--c-bg-card)" }} />
                  <span>
                    <span style={{ display: "block", fontSize: 14, fontWeight: 700, color: "var(--c-text)" }}>{o.title}</span>
                    <span style={{ display: "block", fontSize: 11.5, fontWeight: 500, color: "var(--c-text-soft)", marginTop: 1 }}>{o.desc}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <div style={{ ...CONFIRM_STACK, marginTop: 16 }}>
            <button onClick={() => setRemoveConfirm(null)} style={CONFIRM_CANCEL}>Cancel</button>
            <button disabled={!choice} onClick={() => (choice === "today" ? confirmRemoveTemporary() : confirmRemovePermanent())}
              style={{ ...CONFIRM_DANGER, cursor: choice ? "pointer" : "default", opacity: choice ? 1 : 0.45, transition: "opacity 0.15s" }}>
              Remove
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── View Details panel ───────────────────────────────────────────────────────
  // Opened only from the drawer's "Details" action — pure details, no restart
  // choice mixed in. Restart lives in its own small popup (RestartConfirmModal),
  // reached from the "Done" pill instead.
  const viewDetailsRef = viewDetailsFor ? resolveItemRef(viewDetailsFor.kind, viewDetailsFor.id) : null;

  // ── Custom to-do editor (create + edit) ─────────────────────────────────────
  // Frequency mirrors the habit picker (Daily / Weekly[days|count] / Monthly[dates|count]),
  // minus Yearly — to-dos are short-lived, unlike habits. A "count" (weekly) or
  // monthly-count frequency makes this a Flexible/counted to-do, same as counted habits.
  const CustomTodoEditorModal = () => {
    const d = customTodoEditor;
    if (!d) return null;
    const setD = (patch) => setCustomTodoEditor(p => ({ ...p, ...(typeof patch === "function" ? patch(p) : patch) }));
    // Same treatment as the Habits tab's add/edit form: the panel sits on a
    // pastel theme-tinted background, so fields/buttons drop their border
    // lines in favor of a soft shadow to read as distinct surfaces, and
    // "selected" states become a colored shadow ring instead of a border.
    const FS = {
      input: { ...S.input, border: "none", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
      btn:   { ...S.btn,   border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" },
      label: { ...S.label, color: "var(--c-text-accent)" },
    };
    const pill = (active) => ({ ...FS.btn, flex: 1, fontSize: 12.5, padding: "8px 2px", fontWeight: active ? 700 : 500, background: "var(--c-bg-card)", color: active ? "var(--c-primary)" : "#a07090", boxShadow: active ? "0 0 0 2px var(--c-primary), 0 1px 3px rgba(0,0,0,0.05)" : "0 1px 3px rgba(0,0,0,0.05)" });
    const subPill = (active) => ({ ...FS.btn, flex: 1, fontSize: 11, fontWeight: active ? 700 : 400, background: "var(--c-bg-card)", color: active ? "var(--c-primary)" : "#a07090", boxShadow: active ? "0 0 0 1.5px var(--c-primary), 0 1px 3px rgba(0,0,0,0.05)" : "0 1px 3px rgba(0,0,0,0.05)" });
    const dayCell = (active) => ({ ...FS.btn, fontSize: 11, padding: "8px 0", textAlign: "center", background: "var(--c-bg-card)", color: active ? "var(--c-primary)" : "#a07090", boxShadow: active ? "0 0 0 1.5px var(--c-primary), 0 1px 3px rgba(0,0,0,0.05)" : "0 1px 3px rgba(0,0,0,0.05)" });
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 10056, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "'Quicksand','Nunito',sans-serif" }}>
        {/* No pop-in or backdrop fade: it just appears (Jade, 2026-10-11). */}
        <div style={{ position: "absolute", inset: 0, background: "rgba(40,20,35,0.5)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }} onClick={() => setCustomTodoEditor(null)} />
        <div style={{ position: "relative", background: "var(--c-bg-active)", borderRadius: 22, width: "min(420px, 92vw)", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 14px 50px rgba(212,120,154,0.28)", padding: "18px 20px 22px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, gap: 10 }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: "var(--c-text)" }}>{d.id ? "Edit to-do" : "New to-do"}</span>
            <button onClick={() => setCustomTodoEditor(null)} style={{ background: "var(--c-bg-card)", border: "none", borderRadius: "50%", width: 28, height: 28, cursor: "pointer", color: "var(--c-text-soft)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}><X size={14} /></button>
          </div>

          <input autoFocus value={d.title} onChange={e => setD({ title: e.target.value })} placeholder="What do you want to do?"
            style={{ ...FS.input, width: "100%", boxSizing: "border-box", fontSize: 14, fontWeight: 600 }} />
          <textarea value={d.description} onChange={e => setD({ description: e.target.value })}
            placeholder="Description (optional)…" rows={2}
            style={{ ...FS.input, width: "100%", boxSizing: "border-box", resize: "none", height: 52, marginTop: 8 }} />

          {/* Emoji — optional, same categorized picker as the Habits tab */}
          <p style={{ ...FS.label, marginTop: 12 }}>EMOJI <span style={{ fontWeight: 600, textTransform: "none" }}>(optional)</span></p>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
            <EmojiPicker value={d.emoji} onChange={e => setD({ emoji: e })} />
            {d.emoji && (
              <button onClick={() => setD({ emoji: null })} style={{ ...S.qIconBtn, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}><X size={12} /> Clear</button>
            )}
          </div>

          {/* Frequency — mirrors the habit picker, minus Yearly, plus "Once"
              (to-do only exists today, then never shows up again). */}
          <p style={{ ...FS.label, marginTop: 14 }}>REPEATS</p>
          <div style={{ display: "flex", gap: 5, marginTop: 4 }}>
            {[{ val: "once", label: "Once" }, { val: "daily", label: "Daily" }, { val: "specific", label: "Weekly" }, { val: "monthly", label: "Monthly" }].map(({ val, label }) => {
              const active = d.frequencyType === val || (d.frequencyType === "count" && val === "specific");
              return (
                <button key={val} onClick={() => setD({ frequencyType: d.frequencyType === "count" && val === "specific" ? "specific" : val })}
                  style={pill(active)}>{label}</button>
              );
            })}
          </div>

          {(d.frequencyType === "specific" || d.frequencyType === "count") && (
            <div style={{ marginTop: 8, background: "var(--c-bg-soft)", borderRadius: 10, padding: "10px 12px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                <button onClick={() => setD({ frequencyType: "specific" })} style={subPill(d.frequencyType === "specific")}>📆 Specific days</button>
                <button onClick={() => setD({ frequencyType: "count" })} style={subPill(d.frequencyType === "count")}>🔢 Times/week</button>
              </div>
              {d.frequencyType === "specific" && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 5 }}>
                  {WEEKDAY_LABELS.map((lbl, i) => (
                    <button key={lbl} onClick={() => setD(p => ({ days: p.days.includes(i) ? p.days.filter(x => x !== i) : [...p.days, i] }))}
                      style={dayCell(d.days.includes(i))}>{lbl}</button>
                  ))}
                </div>
              )}
              {d.frequencyType === "count" && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button onClick={() => setD({ weeklyCount: Math.max(1, (d.weeklyCount || 3) - 1) })}
                    style={{ ...FS.btn, width: 30, height: 30, fontSize: 18, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, background: "var(--c-bg-card)" }}>−</button>
                  <span style={{ fontSize: 18, fontWeight: 700, color: "var(--c-text-accent)", minWidth: 26, textAlign: "center" }}>{d.weeklyCount || 3}</span>
                  <button onClick={() => setD({ weeklyCount: Math.min(7, (d.weeklyCount || 3) + 1) })}
                    style={{ ...FS.btn, width: 30, height: 30, fontSize: 18, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, background: "var(--c-bg-card)", color: "var(--c-text-accent)", boxShadow: "0 0 0 1.5px var(--c-primary), 0 1px 3px rgba(0,0,0,0.05)" }}>+</button>
                  <span style={{ fontSize: 12, color: "var(--c-text-mid)" }}>times per week</span>
                </div>
              )}
            </div>
          )}

          {d.frequencyType === "monthly" && (
            <div style={{ marginTop: 8, background: "var(--c-bg-soft)", borderRadius: 10, padding: "10px 12px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                <button onClick={() => setD({ monthlyMode: "dates" })} style={subPill((d.monthlyMode || "dates") === "dates")}>📅 Specific dates</button>
                <button onClick={() => setD({ monthlyMode: "count" })} style={subPill(d.monthlyMode === "count")}>🔢 Times/month</button>
              </div>
              {(d.monthlyMode || "dates") === "dates" && (
                <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  {Array.from({ length: 31 }, (_, i) => (
                    <button key={i + 1} onClick={() => setD(p => ({ monthDates: p.monthDates.includes(i + 1) ? p.monthDates.filter(x => x !== i + 1) : [...p.monthDates, i + 1] }))}
                      style={{ ...dayCell(d.monthDates.includes(i + 1)), width: 30, height: 30, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>{i + 1}</button>
                  ))}
                </div>
              )}
              {d.monthlyMode === "count" && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button onClick={() => setD({ monthlyCount: Math.max(1, (d.monthlyCount || 10) - 1) })}
                    style={{ ...FS.btn, width: 30, height: 30, fontSize: 18, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, background: "var(--c-bg-card)" }}>−</button>
                  <span style={{ fontSize: 18, fontWeight: 700, color: "var(--c-text-accent)", minWidth: 26, textAlign: "center" }}>{d.monthlyCount || 10}</span>
                  <button onClick={() => setD({ monthlyCount: Math.min(31, (d.monthlyCount || 10) + 1) })}
                    style={{ ...FS.btn, width: 30, height: 30, fontSize: 18, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, background: "var(--c-bg-card)", color: "var(--c-text-accent)", boxShadow: "0 0 0 1.5px var(--c-primary), 0 1px 3px rgba(0,0,0,0.05)" }}>+</button>
                  <span style={{ fontSize: 12, color: "var(--c-text-mid)" }}>times per month</span>
                </div>
              )}
            </div>
          )}

          {/* Category — optional link, routes the logged session under it */}
          <p style={{ ...FS.label, marginTop: 14 }}>CATEGORY <span style={{ fontWeight: 600, textTransform: "none" }}>(optional)</span></p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
            <button onClick={() => setD({ goalId: null })}
              style={{ fontFamily: "inherit", fontSize: 12, fontWeight: 700, padding: "6px 12px", borderRadius: 10, border: "none", background: "var(--c-bg-card)", color: !d.goalId ? "var(--c-primary)" : "var(--c-text-soft)", cursor: "pointer", boxShadow: !d.goalId ? "0 0 0 2px var(--c-primary), 0 1px 3px rgba(0,0,0,0.05)" : "0 1px 3px rgba(0,0,0,0.05)" }}>
              None
            </button>
            {categories.map(cat => (
              <button key={cat.id} onClick={() => setD({ goalId: cat.id })}
                style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: "inherit", fontSize: 12, fontWeight: 700, padding: "6px 12px", borderRadius: 10, border: "none", background: "var(--c-bg-card)", color: d.goalId === cat.id ? "var(--c-primary)" : "var(--c-text-soft)", cursor: "pointer", boxShadow: d.goalId === cat.id ? "0 0 0 2px var(--c-primary), 0 1px 3px rgba(0,0,0,0.05)" : "0 1px 3px rgba(0,0,0,0.05)" }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: cat.color || "#caa0d4", flexShrink: 0 }} />
                {cat.emoji ? `${cat.emoji} ` : ""}{cat.title}
              </button>
            ))}
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
            {d.id && (
              <button onClick={() => setShowDeleteTodoConfirm(true)} style={{ ...FS.btn, background: "var(--c-bg-card)", color: "#c0392b", minHeight: "auto", padding: "8px 12px" }}><Trash2 size={13} /></button>
            )}
            <button onClick={() => setCustomTodoEditor(null)} style={{ ...FS.btn, background: "var(--c-bg-card)", flex: 1, minHeight: "auto", padding: "8px 14px" }}>Cancel</button>
            <button onClick={() => saveCustomTodoEditor()} disabled={!d.title.trim()} className="clarity-add-btn"
              style={{ ...S.addBtn, flex: 1, justifyContent: "center", minHeight: "auto", padding: "8px 14px", opacity: d.title.trim() ? 1 : 0.5 }}>
              {d.id ? "Save changes" : "Add to-do"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Delete confirmation — gates the trash button in the custom to-do editor ──
  const DeleteCustomTodoConfirmModal = () => (
    <div style={{ position: "fixed", inset: 0, zIndex: 10057, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "'Quicksand','Nunito',sans-serif" }}>
      <div className="clarity-fade" style={{ position: "absolute", inset: 0, background: "rgba(40,20,35,0.5)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }} onClick={() => setShowDeleteTodoConfirm(false)} />
      <div className="clarity-pop" style={{ position: "relative", background: "var(--c-bg-card)", borderRadius: 20, padding: "24px 22px 18px", maxWidth: 300, width: "100%", textAlign: "center", boxShadow: "0 10px 44px rgba(0,0,0,0.22)" }}>
        <ConfirmHeader title="Are you sure you want to delete this to-do?" />
        <div style={CONFIRM_STACK}>
          <button onClick={() => setShowDeleteTodoConfirm(false)} style={CONFIRM_CANCEL}>Cancel</button>
          <button onClick={deleteCustomTodoFromEditor} style={CONFIRM_DANGER}>Delete</button>
        </div>
      </div>
    </div>
  );

  // ── "Just a category" Remove confirmation (the clock restarts, so ask first) ──
  const CategoryRemoveConfirmModal = () => (
    <div style={{ ...CONFIRM_OVERLAY, zIndex: 10057 }}>
      <div className="clarity-fade" style={CONFIRM_DIM} onClick={() => setShowCategoryRemoveConfirm(false)} />
      <div className="clarity-pop" style={CONFIRM_CARD}>
        <ConfirmHeader icon={MinusCircle} title={`Remove "${planCurrent?.title}"?`} subtitle="The timer will restart. The category itself isn't deleted." />
        <div style={CONFIRM_STACK}>
          <button onClick={() => setShowCategoryRemoveConfirm(false)} style={CONFIRM_CANCEL}>Keep it</button>
          <button onClick={() => { setShowCategoryRemoveConfirm(false); rememberCategory(null); exitPlan(); }} style={CONFIRM_DANGER}>Remove</button>
        </div>
      </div>
    </div>
  );

  if (showStats) return statsPage;

  // ── Top bar: ⋯ (only when it has something in it), Select, Chart ──────────────
  // The actions that used to float in the timer card's corners (Previous, View
  // details, Edit timer duration, Hide duration, Remove) live in the ⋯ menu now.
  const ci = clockInInk();
  const ciHasPrev = plan && planCurrent && plan.items.length > 1 && reviewFrom() > 0;
  // A note on the session just logged (Done state). It used to be a pill under the Done
  // pill and sat on top of its pop (Jade, 2026-10-24), so it lives in the ⋯ menu now.
  const ciNoteSess = (plan && planReview === null && planCurrent && plan.doneCurrent && !isCategoryItem(planCurrent)
    && plan.lastLog?.sessionId) ? focusSessions.find(x => x.id === plan.lastLog.sessionId) : null;
  const ciMenuItems = [
    ...(ciHasPrev ? [{ key: "prev", label: "Previous", Icon: ChevronLeft, onClick: goPrevPlan }] : []),
    ...(plan && planCurrent && !isCategoryItem(planCurrent) ? [{ key: "details", label: "View details", Icon: Info, onClick: () => setViewDetailsFor({ qid: planCurrent.qid, kind: planCurrent.kind, id: planCurrent.id, title: planCurrent.title }) }] : []),
    ...(ciNoteSess ? [{ key: "note", label: ciNoteSess.note ? "Edit note" : "Add note", Icon: StickyNote, onClick: () => openNoteEditor({ sid: ciNoteSess.id, draft: ciNoteSess.note || "" }) }] : []),
    ...(canEditTimerDuration ? [{ key: "editDuration", label: "Edit timer duration", Icon: Pencil,
      // Once the session is logged (Done), the time left is nothing to lose: go
      // straight to the picker for the next session, no warning (Jade, 2026-10-24).
      onClick: () => (plan && planCurrent && plan.doneCurrent) ? handleReset() : setEditDurationConfirm(true) }] : []),
    ...(mode === "stopwatch" ? [{ key: "hideDuration", label: hideStopwatchTime ? "Show duration" : "Hide duration", Icon: hideStopwatchTime ? Eye : EyeOff, onClick: toggleHideStopwatchTime }] : []),
    ...(plan && planCurrent ? [{ key: "remove", label: "Remove", Icon: Trash2, danger: true, onClick: () => isCategoryItem(planCurrent)
      ? setShowCategoryRemoveConfirm(true)
      : setRemoveConfirm({ qid: planCurrent.qid, kind: planCurrent.kind, id: planCurrent.id, title: planCurrent.title }) }] : []),
  ];
  const openCiMenu = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    // Just under the button, right edges lined up, like the Chart page's ⋯ menu (Jade, 2026-10-24).
    setCiMenu({ top: r.bottom - 9, right: Math.max(8, window.innerWidth - r.right) });
  };
  const ciTopButtons = (
    <>
      {/* Custom / Elapsed: the current mode beside the menu button, with a swap icon so
          it reads as a switch; a tap flips it (Jade, 2026-10-24). It replaced the pill
          switcher. While a session counts, requestModeSwitch asks first. */}
      {/* Desktop too: at the top left, beside the menu button (Jade, 2026-11-02; it sat
          under the time for a few hours first). */}
      {(
      <button className="ci-mode-btn" data-tour="clockin-mode-btn" onClick={() => requestModeSwitch(mode === "stopwatch" ? "custom" : "stopwatch")}
        title={mode === "stopwatch" ? "Switch to Custom" : "Switch to Elapsed"} aria-label={mode === "stopwatch" ? "Elapsed. Switch to Custom" : "Custom. Switch to Elapsed"}
        style={ciTitleBtn(ci)}>
        {mode === "stopwatch" ? "Elapsed" : "Custom"}
        <ArrowLeftRight size={16} strokeWidth={2.6} style={{ flexShrink: 0 }} />
      </button>
      )}
      {ciMenuItems.length > 0 && (
        <button onClick={openCiMenu} title="More options" aria-label="More options" style={ciRoundBtn(ci)}>
          <MoreHorizontal size={22} strokeWidth={2.6} />
        </button>
      )}
      <button data-tour="clockin-todo-btn" title={todoLabel} aria-label="Select"
        onClick={() => { if (plan && planCurrent) editPlan(); else { setQueueTab("habits"); queueTabDirRef.current = 0; setShowQueuePanel(true); } }}
        style={ciRoundBtn(ci)}>
        {allQueueDone && !(plan && planCurrent) ? <PhCheckCircle size={20} weight="fill" /> : <ListChecks size={23} strokeWidth={2.4} />}
      </button>
      <button data-tour="clockin-chart-btn" onClick={() => setShowStats(true)} title="Chart" aria-label="Chart" style={ciRoundBtn(ci)}>
        <StatsBarsIcon size={22} />
      </button>
    </>
  );
  const ciMenuPortal = ciMenu && ciMenuItems.length > 0 && createPortal(
    <>
      <div onClick={() => setCiMenu(null)} style={{ position: "fixed", inset: 0, zIndex: 9998 }} />
      <div style={{ position: "fixed", top: ciMenu.top, right: ciMenu.right, zIndex: 9999, minWidth: 190, overflow: "hidden", borderRadius: 14, background: "var(--c-bg-card)", border: "1.5px solid var(--c-border)", boxShadow: "0 4px 20px var(--c-shadow)", fontFamily: "'Quicksand', sans-serif" }}>
        {ciMenuItems.map((it, i) => (
          <button key={it.key} onClick={() => { setCiMenu(null); it.onClick(); }}
            style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", minHeight: 0, padding: "10px 14px", border: "none", borderTop: i > 0 ? "1px solid var(--c-border-light)" : "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 700, color: it.danger ? "#e53935" : "var(--c-text)", textAlign: "left" }}>
            <it.Icon size={14} /> {it.label}
          </button>
        ))}
      </div>
    </>, document.body);

  const focusAmountHabit = focusAmountSheetId ? habits.find(x => x.id === focusAmountSheetId) : null;

  // Desktop, before Start, with nothing or a category picked: the pick, the session
  // name and Start sit in one row under the time (Jade's mockup C, 2026-11-02).
  // Habits and tasks keep the usual layout (their progress / sub-tasks need the room).
  const openSelectPanel = () => { if (plan && planCurrent) editPlan(); else { setQueueTab("habits"); queueTabDirRef.current = 0; setShowQueuePanel(true); } };
  const deskRow = wide && !(mode === "stopwatch" ? swRunning : running) && planReview === null
    && (!plan || !planCurrent || (isCategoryItem(planCurrent) && !plan.doneCurrent));
  const deskRowEl = !deskRow ? null : (() => {
    const isCat = plan && planCurrent && isCategoryItem(planCurrent);
    const dotColor = isCat ? (categories.find(c => c.id === planCurrent.id)?.color || planCurrent.color) : null;
    const startDisabled = mode !== "stopwatch" && customEmpty;
    const lastSess = isCat && plan.lastLog?.sessionId ? focusSessions.find(x => x.id === plan.lastLog.sessionId) : null;
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
        <style>{"input.ci-title:focus { box-shadow: none !important; outline: none !important; border-color: transparent !important; } input.ci-title::placeholder { color: var(--c-text-soft); opacity: 0.7; }"}</style>
        <div className="ci-glass" style={{ ...ci.glass, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: 10, padding: 8, borderRadius: 20, maxWidth: "100%" }}>
          <button type="button" onClick={openSelectPanel} data-tour="clockin-todo-btn-row" title="Choose what to time"
            style={{ display: "inline-flex", alignItems: "center", gap: 8, height: 48, minHeight: 0, padding: "0 16px", borderRadius: 14, border: "none", cursor: "pointer",
              background: ci.dark ? "rgba(255,255,255,0.18)" : "#ffffff", color: ci.ink, fontFamily: "'Nunito', sans-serif", fontSize: 16, fontWeight: 800, maxWidth: 260 }}>
            {dotColor && <span style={{ width: 10, height: 10, borderRadius: "50%", background: dotColor, flexShrink: 0 }} />}
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{isCat ? planCurrent.title : "Select"}</span>
            <ChevronDown size={16} strokeWidth={2.6} style={{ flexShrink: 0 }} />
          </button>
          {isCat && (
            <input className="ci-title" value={planCurrent.sessionTitle || ""} maxLength={SESSION_TITLE_MAX}
              placeholder="Name this session (optional)" aria-label="Session title" enterKeyHint="done"
              onChange={e => { const v = e.target.value; setPlan(p => p && ({ ...p, items: p.items.map(x => isCategoryItem(x) ? { ...x, sessionTitle: v } : x) })); }}
              onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }}
              style={{ width: 260, height: 48, boxSizing: "border-box", border: "none", background: "transparent", padding: "0 12px", fontFamily: "inherit", fontSize: 15, fontWeight: 600, color: "var(--c-text)" }} />
          )}
          <button onClick={mode === "stopwatch" ? handleSwStartPause : handleStartPause} disabled={startDisabled} className="clarity-add-btn"
            style={{ ...S.addBtn, height: 48, minHeight: 0, padding: "0 26px", fontSize: 16, borderRadius: 14, boxShadow: "none", opacity: startDisabled ? 0.5 : 1, cursor: startDisabled ? "default" : "pointer" }}>
            <PlayCircle size={18} /> Start
          </button>
        </div>
        {lastSess && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
            {lastSess.note ? (
              <button onClick={() => openNoteEditor({ sid: lastSess.id, draft: lastSess.note || "" })} title="Edit note"
                style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700, color: "var(--c-text-accent)", display: "inline-flex", alignItems: "center", gap: 6, maxWidth: 280 }}>
                <StickyNote size={14} style={{ flexShrink: 0 }} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lastSess.note}</span>
              </button>
            ) : (
              <NoteAddPill title={`Add a note to your last session (${lastSess.minutes} min)`} onClick={() => openNoteEditor({ sid: lastSess.id, draft: "" })} />
            )}
          </div>
        )}
      </div>
    );
  })();

  return (
    <div className="focus-dashboard ci-desk-center" style={isDesktop
      ? { display: "flex", gap: 30, alignItems: "flex-start", justifyContent: "center", flexWrap: "wrap", maxWidth: 1000, margin: "0 auto", position: "relative" }
      : {}}>
    <div className="focus-timer-col" style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: wide ? 760 : 420, width: "100%", margin: isDesktop ? 0 : "0 auto", flex: isDesktop ? "0 1 420px" : undefined }}>
      {focusAmountHabit && (
        <AddAmountSheet habit={focusAmountHabit} rawLog={habitLogs[qToday]?.[focusAmountHabit.id]} dateLabel={focusAmountAtStart ? "Today's progress" : "Session done. How much today?"}
          color={categories.find(c => c.id === focusAmountHabit.goalId)?.color || focusAmountHabit.color}
          onLog={(v) => setHabitLogs(prev => ({ ...prev, [qToday]: { ...(prev[qToday] || {}), [focusAmountHabit.id]: v } }))}
          onClose={() => { setFocusAmountSheetId(null); if (plan?.doneCurrent && planCurrent?.id === focusAmountHabit.id) setJustCompletedAnim(true); }} />
      )}
      {showFinishModal && createPortal(FinishModal(), document.body)}
      {/* Today's plan panel unmounts (not just hides) while the custom to-do editor is
          open, so the dimmed panel isn't visible behind it — it reappears on its own
          once the editor closes, since showQueuePanel itself never changes. */}
      {showQueuePanel && !customTodoEditor && createPortal(QueuePanel(), document.body)}
      {queueSummary && createPortal(QueueSummaryModal(), document.body)}
      {restartConfirm && createPortal(RestartConfirmModal(), document.body)}
      {quickResetConfirm && createPortal(QuickResetConfirmModal(), document.body)}
      {editDurationConfirm && createPortal(EditDurationConfirmModal(), document.body)}
      {modeSwitchConfirm && createPortal(ModeSwitchConfirmModal(), document.body)}
      {removeConfirm && createPortal(RemoveConfirmModal(), document.body)}
      {showCategoryRemoveConfirm && createPortal(CategoryRemoveConfirmModal(), document.body)}
      {undoDoneConfirm && createPortal(UndoDoneConfirmModal(), document.body)}
      {viewDetailsFor && viewDetailsRef && createPortal(
        <ItemDetailsModal
          kind={viewDetailsFor.kind} itemRef={viewDetailsRef} categories={categories}
          focusSessions={focusSessions} habitLogs={habitLogs} targetDate={qToday} hideDuration={hideSessionDuration}
          setTasks={setTasks}
          onSetSessionNote={(sess, text) => setFocusSessions(p => withSessionNote(p, sess, text))}
          onEditSessionTimes={(sess, start, end, estimated) => {
            const delta = minutesBetween(start, end) - (sess.minutes || 0);
            setFocusSessions(p => withSessionTimes(p, sess, start, end, estimated));
            setHabitLogs(prev => habitLogsAfterSessionChange(prev, habits, sess, delta));
          }}
          onRemoveSession={(sess) => {
            setFocusSessions(p => withoutSession(p, sess));
            setHabitLogs(prev => habitLogsAfterSessionChange(prev, habits, sess, -(sess.minutes || 0)));
          }}
          onClose={() => setViewDetailsFor(null)}
        />, document.body)}
      {(
        <SessionSheetHost ref={addPastRef} mode="add"
          habits={habits.filter(h => (h.habitType || "todo") === "todo" && !h.archived)}
          tasks={tasks} categories={categories}
          onSave={({ target, date, start, end, note, estimated }) => {
            const sess = pastSession({ target, date, start, end, note, estimated, categories, id: `s_${Date.now()}` });
            setFocusSessions(p => [...p, sess]);
            // A Time habit's minutes for that day follow its sessions, like a live one.
            setHabitLogs(prev => habitLogsAfterSessionChange(prev, habits, sess, sess.minutes));
            const what = target ? (target.ref.name || target.ref.title) : null;
            sonnerToast(what ? `Added · ${sess.minutes} min of ${what}` : `Added · ${sess.minutes} min`);
          }} />
      )}
      {noteEditor && (() => {
        const sess = focusSessions.find(x => x.id === noteEditor.sid);
        if (!sess) return null;
        return (
          <SessionNoteBar key={noteEditor.sid} note={sess.note || ""} label={sess.label ? sess.label.replace(/^(Habit|Task|To-do): /, "") : ""}
            onSave={(text) => setFocusSessions(p => withSessionNote(p, sess, text))}
            onClose={() => setNoteEditor(null)} />
        );
      })()}
      {customTodoEditor && createPortal(CustomTodoEditorModal(), document.body)}
      {showDeleteTodoConfirm && customTodoEditor && createPortal(DeleteCustomTodoConfirmModal(), document.body)}
      {!isDesktop && <TabHeader
        title="Clock In"
        gradient="linear-gradient(125deg, #d08000 0%, #e05818 100%)"
        subtextColor="#c07010"
        subtext=""
        iconButtons={[]}
      />}

      {/* Select / Chart / more-options buttons: in the app's top bar beside the
          menu button on a phone (Jade, 2026-10-24), else a row above the timer. */}
      {topBarSlotNode ? createPortal(ciTopButtons, topBarSlotNode)
        : <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>{ciTopButtons}</div>}
      {ciMenuPortal}

      {/* ── The timer, straight on the page background: no card (Jade, 2026-10-24).
          Ring, picked item, then the controls, centred in the
          space between the top bar and the tab bar. The --c-* overrides turn the
          text black or white to suit the background, and give the small round
          buttons a frosted surface. */}
      <div className="ci-stage" style={{
        "--c-text": ci.ink, "--c-text-mid": ci.ink, "--c-text-soft": ci.soft,
        "--c-bg-card": ci.glass.background, "--c-border-mid": ci.faint,
        // Accent text (Add past session, Block apps, Add note) in a deeper shade of the
        // accent: some backgrounds' own accent is too pale to read off the page (Terra).
        "--c-text-accent": ci.dark ? "#ffffff" : "color-mix(in srgb, var(--c-primary) 72%, #000)",
        position: "relative", width: "100%",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        minHeight: CI_STAGE_MIN_H,
      }}>

        {/* Circular timer / stopwatch. In custom mode, the ring itself doubles as
            the duration picker before Start is pressed, so it stays visible even
            with "Hide duration" on — there's nothing to protect yet. Once running,
            "Hide duration" (from Settings ▸ Clock-In Options) hides the ring
            entirely, same as before. */}
        {showRing && (
          <div style={{ width: "100%" }}>
            {/* Called as plain functions, not JSX (<TimerDisplay/>) — these are
                defined as nested functions inside FocusTab's body, so JSX
                invocation makes React treat every render as a brand-new
                component type and fully remount the subtree, wiping out the
                ring picker's internal drag state after every single step
                (which is exactly what made dragging feel like it kept
                "stopping" instead of tracking the pointer continuously).
                A plain function call just inlines the returned JSX with no
                separate fiber/mount boundary at all. */}
            {mode === "stopwatch" ? StopwatchDisplay() : TimerDisplay()}
          </div>
        )}

        {/* The current plan item (or the one being reviewed), under the ring, above Start (Jade, 2026-10-24). */}
        {!deskRow && plan && ((planReview !== null && plan.items[planReview]) || planCurrent) && (
        <div style={{ width: "100%", margin: "18px 0 39px" /* same visible gap above (ring) and below (buttons), Jade 2026-10-24 */ }}>
          {plan && planReview !== null && plan.items[planReview] ? (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", color: "#4caf50" }}>Completed ✓ · {planReview + 1}/{plan.items.length}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "var(--c-text)", marginTop: 4, lineHeight: 1.25, textDecoration: "line-through", opacity: 0.6 }}>{plan.items[planReview].title}</div>
            </div>
          ) : plan && planCurrent ? (
            <div style={{ textAlign: "center" }}>
              {isCategoryItem(planCurrent) && (
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.2, textTransform: "uppercase", color: "var(--c-text-soft)" }}>Category</div>
              )}
              <div style={{ fontSize: 22, fontWeight: 800, color: "var(--c-text)", marginTop: 4, lineHeight: 1.25 }}>{planCurrent.title}</div>
              {/* Optional session title, Forest-style (Jade, 2026-10-10): looks like
                  faint text, not a box, so the card stays uncluttered. Saved as the
                  session's name; blank = the category name. */}
              {isCategoryItem(planCurrent) && (
                <>
                  <style>{"input.ci-title:focus { box-shadow: none !important; outline: none !important; border-color: transparent !important; } input.ci-title::placeholder { color: var(--c-text-soft); opacity: 0.7; }"}</style>
                  <input className="ci-title" value={planCurrent.sessionTitle || ""} maxLength={SESSION_TITLE_MAX}
                    placeholder="Name this session (optional)" aria-label="Session title" enterKeyHint="done"
                    onChange={e => { const v = e.target.value; setPlan(p => p && ({ ...p, items: p.items.map(x => isCategoryItem(x) ? { ...x, sessionTitle: v } : x) })); }}
                    onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    style={{ display: "block", width: "100%", maxWidth: 300, margin: "2px auto 0", border: "none", background: "transparent", textAlign: "center", fontFamily: "inherit", fontSize: 14, fontWeight: 600, color: "var(--c-text-mid)", padding: "4px 0" }} />
                </>
              )}
              {(() => {
                const h = planCurrent.kind === "habit" ? habits.find(x => x.id === planCurrent.id) : null;
                const progress = h && isAmountHabit(h) ? `${formatProgress(h, loggedAmount(h, habitLogs[qToday]?.[h.id]))} today` : null;
                // Tap to open the Add amount sheet again, e.g. after closing the one
                // Start opened (Jade, 2026-10-22). Time habits stay plain text: the
                // session itself adds their minutes.
                if (progress && unitOf(h) !== "time") return (
                  <button type="button" onClick={() => { setFocusAmountAtStart(true); setFocusAmountSheetId(h.id); }} title="Add amount"
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 6, minHeight: 0, minWidth: 0, padding: "3px 11px 3px 9px", borderRadius: 999,
                      border: "1.5px solid var(--c-text-accent)", background: "var(--c-bg-card)", color: "var(--c-text-accent)", fontFamily: "inherit", fontSize: 11.5, fontWeight: 800, cursor: "pointer" }}>
                    <span style={{ fontSize: 14, lineHeight: 1 }}>+</span>{progress}
                  </button>
                );
                if (progress) return (
                  <div style={{ fontSize: 11, fontWeight: 800, color: "var(--c-text-accent)", marginTop: 3 }}>{progress}</div>
                );
                return planCurrent.dailyCount > 1 && (
                  <div style={{ fontSize: 11, fontWeight: 800, color: "var(--c-text-accent)", marginTop: 3 }}>
                    {h ? habitDoneCount(h) : 0}/{planCurrent.dailyCount} today
                  </div>
                );
              })()}
              {planNext && plan.doneCurrent && focusAmountSheetId !== planCurrent.id && <div style={{ fontSize: 11, color: "var(--c-text-soft)", marginTop: 4 }}>next: {planNext.title}</div>}
              {(() => {
                if (planCurrent.kind !== "task") return null;
                const liveTask = resolveItemRef("task", planCurrent.id);
                if (!liveTask?.subtasks?.length) return null;
                return (
                  <div style={{ marginTop: 10, textAlign: "left" }}>
                    <button onClick={() => setShowInlineSubtasks(v => !v)}
                      style={{ display: "flex", alignItems: "center", gap: 5, margin: "0 auto", background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit", color: "var(--c-text-soft)", fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", padding: "4px 0" }}>
                      {showInlineSubtasks ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Sub-tasks ({liveTask.subtasks.filter(s => s.done).length}/{liveTask.subtasks.length})
                    </button>
                    {showInlineSubtasks && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
                        {liveTask.subtasks.map(s => (
                          <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 9, padding: "6px 10px", borderRadius: 10, background: "var(--c-bg-soft)" }}>
                            <span style={{ padding: 6, margin: -6, display: "flex", flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                              <HabitCheckbox checked={!!s.done} color="#d4789a" size={20} burst={false} onChange={() => toggleInlineSubtask(liveTask.id, s.id)} />
                            </span>
                            <span style={{ flex: 1, fontSize: 13, color: "var(--c-text-mid)", fontWeight: 600, textDecoration: s.done ? "line-through" : "none", opacity: s.done ? 0.55 : 1, wordBreak: "break-word" }}>{s.title}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          ) : null}
        </div>
        )}
        {(deskRow || !(plan && ((planReview !== null && plan.items[planReview]) || planCurrent))) && <div style={{ height: deskRow ? 44 : 30 }} />}

        {/* Everything under the title (Block apps, the controls, Add past session) sits in
            one box of a fixed height, so the ring above doesn't move when Start swaps for
            the running buttons or Done (Jade, 2026-10-24: "its size shouldn't change"). */}
        <div className="ci-controls" style={{ width: "100%", minHeight: CI_CONTROLS_H, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start" }}>
        {/* App Blocker — per-session toggle, only shown once there's actually
            something it could do (permissions on + apps picked in Settings).
            Hidden entirely once the session is running — the checkbox is
            locked at that point anyway (see toggleBlockAppsThisSession), and
            the cover screen itself already makes it obvious blocking is on,
            so a status line here was just redundant chrome. */}
        {appBlockerIsNative && appBlockerReady && (() => {
          const sessionRunning = mode === "stopwatch" ? swRunning : running;
          if (sessionRunning) return null;
          return (
            // No bottom margin, and the divider below moves up to it: there was a 14px +
            // 16px empty gap above Start (Jade, 2026-10-16).
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "4px 0 0" }}>
                <input
                  type="checkbox"
                  id="blockAppsThisSessionCheckbox"
                  checked={blockAppsThisSession}
                  onChange={toggleBlockAppsThisSession}
                  style={{ margin: 0, flexShrink: 0 }}
                />
                <button
                  onClick={onOpenAppBlockerSettings}
                  title="Open Settings to choose which apps"
                  style={{
                    border: "none", background: "none", padding: 0, cursor: "pointer",
                    fontFamily: "inherit", fontSize: 12.5, fontWeight: 700,
                    color: "var(--c-text-accent)", textDecoration: "underline", textUnderlineOffset: 2,
                    textAlign: "left",
                  }}
                >
                  Block apps for this session
                </button>
              </div>
            </div>
          );
        })()}


        {/* Controls — plan-aware (Start → restart/pause/check → Done + Next) */}
        {deskRow ? deskRowEl : plan && planReview !== null && plan.items[planReview] ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 18 }}>
            <button onClick={() => setRestartConfirm(plan.items[planReview])} title="Restart or continue this one"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "11px 22px", borderRadius: 16, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 14, fontWeight: 800, color: "#fff", background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))", boxShadow: "0 4px 14px rgba(212,120,154,0.35)" }}>
              <RotateCcw size={16} /> Restart
            </button>
            <button onClick={goForwardReview} title="Back to current"
              style={{ width: 46, height: 46, borderRadius: "50%", border: "1.5px solid var(--c-border-mid)", background: "var(--c-bg-card)", color: "var(--c-text-mid)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <ChevronRight size={20} />
            </button>
          </div>
        ) : plan && planCurrent ? (
          plan.doneCurrent ? (
            // Hidden (space kept) while the Add amount sheet for this habit is up.
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, visibility: focusAmountSheetId && focusAmountSheetId === planCurrent.id ? "hidden" : undefined }}>
              <button onClick={() => setRestartConfirm(planCurrent)} title="Tap to undo"
                ref={donePillRef} className={justCompletedAnim ? "clarity-done-pill sheet-enter-wait" : undefined}
                style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "13px 32px", borderRadius: 20, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 15, fontWeight: 800, color: "var(--c-on-primary)", background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))", boxShadow: "0 4px 14px var(--c-shadow)" }}>
                {/* Solid accent, like Start and the check button (Jade, 2026-10-24); plain
                    green text faded into several backgrounds, then solid green was off-theme. */}
                <CheckCircle size={18} /> Done
              </button>
            </div>
          ) : (mode === "stopwatch" ? swRunning : running) ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 24 }}>
              <button onClick={() => setQuickResetConfirm(true)} title="Restart"
                style={{ width: 46, height: 46, borderRadius: "50%", border: "1.5px solid var(--c-border-mid)", background: "var(--c-bg-card)", color: "var(--c-text-mid)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <RotateCcw size={18} />
              </button>
              <button onClick={handlePlanCheck} title="Mark complete" className="clarity-check-btn"
                style={{ width: 64, height: 64, borderRadius: "50%", border: "none", cursor: "pointer", background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))", padding: 0, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Check size={28} strokeWidth={3} color="#fff" />
              </button>
              <button onClick={mode === "stopwatch" ? handleSwStartPause : handleStartPause} title={(mode === "stopwatch" ? swPaused : paused) ? "Resume" : "Pause"}
                style={{ width: 46, height: 46, borderRadius: "50%", border: "1.5px solid var(--c-border-mid)", background: "var(--c-bg-card)", color: "var(--c-text-mid)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {(mode === "stopwatch" ? swPaused : paused) ? <PlayCircle size={22} /> : <PauseCircle size={22} />}
              </button>
            </div>
          ) : (
            <>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button onClick={mode === "stopwatch" ? handleSwStartPause : handleStartPause} disabled={customEmpty} className="clarity-add-btn"
                style={{ ...S.addBtn, justifyContent: "center", paddingLeft: 46, paddingRight: 46, fontSize: 15, borderRadius: 16, opacity: customEmpty ? 0.5 : 1, cursor: customEmpty ? "default" : "pointer" }}>
                <PlayCircle size={18} /> Start
              </button>
            </div>
            {/* After a "Just a category" session is saved: add a note to it. */}
            {isCategoryItem(planCurrent) && (() => {
              const sid = plan.lastLog?.sessionId;
              const sess = sid && focusSessions.find(x => x.id === sid);
              if (!sess) return null;
              return (
                <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
                  {sess.note ? (
                    <button onClick={() => openNoteEditor({ sid, draft: sess.note || "" })} title="Edit note"
                      style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700, color: "var(--c-text-accent)", display: "inline-flex", alignItems: "center", gap: 6, maxWidth: 280 }}>
                      <StickyNote size={14} style={{ flexShrink: 0 }} />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sess.note}</span>
                    </button>
                  ) : (
                    <NoteAddPill title={`Add a note to your last session (${sess.minutes} min)`} onClick={() => openNoteEditor({ sid, draft: "" })} />
                  )}
                </div>
              );
            })()}
            </>
          )
        ) : !(mode === "stopwatch" ? swRunning : running) ? (
          // Before Start: only Start, centred. Restart and Done show once it runs (Jade, 2026-10-24).
          <div style={{ display: "flex", justifyContent: "center" }}>
            <button onClick={mode === "stopwatch" ? handleSwStartPause : handleStartPause} disabled={mode !== "stopwatch" && customEmpty} className="clarity-add-btn"
              style={{ ...S.addBtn, justifyContent: "center", paddingLeft: 46, paddingRight: 46, fontSize: 15, borderRadius: 16, opacity: (mode !== "stopwatch" && customEmpty) ? 0.5 : 1, cursor: (mode !== "stopwatch" && customEmpty) ? "default" : "pointer" }}>
              <PlayCircle size={18} /> Start
            </button>
          </div>
        ) : (
          // A running session with nothing assigned gets the same three round buttons as an
          // assigned one (Jade, 2026-10-25): Restart, the check (= Done: the finish panel),
          // Pause / Resume.
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 24 }}>
            <button onClick={() => setQuickResetConfirm(true)} title="Restart"
              style={{ width: 46, height: 46, borderRadius: "50%", border: "1.5px solid var(--c-border-mid)", background: "var(--c-bg-card)", color: "var(--c-text-mid)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <RotateCcw size={18} />
            </button>
            <button onClick={() => { if (canFinish) setShowFinishModal(true); }} title="Done" className="clarity-check-btn"
              style={{ width: 64, height: 64, borderRadius: "50%", border: "none", cursor: "pointer", background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))", padding: 0, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Check size={28} strokeWidth={3} color="#fff" />
            </button>
            <button onClick={mode === "stopwatch" ? handleSwStartPause : handleStartPause} title={(mode === "stopwatch" ? swPaused : paused) ? "Resume" : "Pause"}
              style={{ width: 46, height: 46, borderRadius: "50%", border: "1.5px solid var(--c-border-mid)", background: "var(--c-bg-card)", color: "var(--c-text-mid)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {(mode === "stopwatch" ? swPaused : paused) ? <PlayCircle size={22} /> : <PauseCircle size={22} />}
            </button>
          </div>
        )}

        {/* Log time that wasn't clocked live (Jade, 2026-10-11). Only while
            nothing is running, so it never competes with the live controls. */}
        {!running && !paused && !swRunning && !swPaused && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
            <button type="button" onClick={() => addPastRef.current && addPastRef.current.open()}
              style={{ background: "none", border: "none", minHeight: 0, padding: "4px 6px", cursor: "pointer", fontFamily: "inherit", fontSize: 13.5, fontWeight: 700, color: "var(--c-text-accent)" }}>
              + Add past session
            </button>
          </div>
        )}
        </div>

      </div>
    </div>
    </div>
  );
}
